/**
 * Traductor de frases a parámetros del Constructor de Gráficas.
 *
 * Determinista (no usa IA): diccionarios de palabras -> conceptos -> campos del
 * catálogo real (`configuraciones/catalogo-metricas/`). Ejemplo:
 * "cuántos empleados por dependencia, solo sindicalizados" ->
 * dimensión Dependencia, métrica Número de Empleados (COUNT), filtros
 * Estado del Empleado (vigentes) y Sindicalizado = Sí.
 *
 * IMPORTANTE: al agregar entradas nuevas a `CatalogoMetrica` (migraciones del
 * backend), agrega aquí las palabras con las que la gente las nombraría
 * (METRICAS, DIMENSIONES o FILTROS); si no, el campo existe pero la frase no
 * lo encuentra (sigue disponible a mano en el formulario).
 */

// ancla -> nombre legible de la tabla y módulo (sección del Dashboard) donde conviene crearla.
export const TABLAS = {
  'entidad.Empleado': ['Empleado', 'entidad'],
  'entidad.HistoricoSBCEmpleado': ['Histórico del SBC', 'entidad'],
  'nominas.IncidenciaEmpleadoDetalle': ['Incidencias (ausentismo)', 'entidad'],
  'catalogos.DatoOperacionalDelEmpleado': ['Dato operacional del empleado', 'nominas'],
  'catalogos.Plaza': ['Plaza', 'nominas'],
  'nominas.NominaPlaza': ['Nómina por plaza', 'nominas'],
  'nominas.NominaPlazaPercepcion': ['Percepciones por plaza', 'nominas'],
  'nominas.NominaPlazaDeduccion': ['Deducciones por plaza', 'nominas'],
  'nominas.NominaPlazaDescuento': ['Descuentos por plaza', 'nominas'],
  'nominas.NominaPlazaIMSS': ['IMSS por plaza', 'nominas'],
  'nominas.NominaPlazaIPEJAL': ['IPEJAL por plaza', 'nominas'],
  'nominas.CalculoFuenteFinanciamiento': ['Cálculo por fuente de financiamiento', 'nominas'],
  'nominas.DescuentoEmpleado': ['Cartera de descuentos', 'nominas'],
  'relojchecador.CargaDeReporteDetalle': ['Registro de asistencia', 'relojchecador'],
  'relojchecador.CargaDeReporte': ['Cargas del reloj', 'relojchecador'],
  'relojchecador.CargaDeReporteErrorAPI': ['Errores de la API del reloj', 'relojchecador'],
  'catalogos.AdministracionDePlaza': ['Asignación de plazas', 'plazas'],
  'catalogos.HistorialSueldoPlaza': ['Historial de sueldos de plaza', 'plazas'],
  'catalogos.PlazaCosto': ['Costo de plaza', 'plazas'],
  'catalogos.AutorizacionPlazaDetalle': ['Autorizaciones de asignación', 'plazas'],
  'catalogos.HistoricoDeSindicato': ['Afiliaciones a sindicato', 'plazas'],
  'nominas.Nomina': ['Nóminas (cabecera)', 'operacion_nomina'],
  'nominas.SolicitudPercepcionEmpleado': ['Solicitudes de percepción', 'operacion_nomina'],
  'nominas.RegistroDiasGuardia': ['Guardias', 'operacion_nomina'],
  'nominas.DescuentoCargaExternaArchivoEmpleado': ['Cargas externas', 'operacion_nomina'],
  'catalogos.ErrorCargarPlantilla': ['Errores de importación', 'operacion_nomina'],
};

// Cuando varias tablas sirven, se prefiere la de mayor prioridad.
const PRIORIDAD_TABLA = {
  'entidad.Empleado': 9,
  'nominas.NominaPlaza': 8,
  'catalogos.AdministracionDePlaza': 6,
  'relojchecador.CargaDeReporteDetalle': 6,
  'nominas.IncidenciaEmpleadoDetalle': 6,
  'catalogos.Plaza': 5,
  'nominas.NominaPlazaPercepcion': 5,
  'nominas.Nomina': 4,
  'nominas.SolicitudPercepcionEmpleado': 4,
  'catalogos.HistorialSueldoPlaza': 4,
  'nominas.NominaPlazaIMSS': 4,
  'nominas.NominaPlazaIPEJAL': 4,
  'nominas.NominaPlazaDescuento': 4,
  'nominas.NominaPlazaDeduccion': 4,
  'nominas.RegistroDiasGuardia': 4,
  'nominas.DescuentoCargaExternaArchivoEmpleado': 4,
  'catalogos.PlazaCosto': 3,
  'nominas.CalculoFuenteFinanciamiento': 3,
  'catalogos.DatoOperacionalDelEmpleado': 1,
};

const TABLAS_DE_NOMINA = [
  'nominas.NominaPlaza', 'nominas.NominaPlazaPercepcion', 'nominas.NominaPlazaDeduccion',
  'nominas.NominaPlazaDescuento', 'nominas.NominaPlazaIMSS', 'nominas.NominaPlazaIPEJAL',
  'nominas.CalculoFuenteFinanciamiento',
];

const DO = 'dato_operacional_del_empleado__';
const FECHA_FINAL_NOMINA = 'nomina__fecha_final_nomina';

const FECHA_PRINCIPAL = {
  'entidad.Empleado': `${DO}fecha_de_alta`,
  'catalogos.DatoOperacionalDelEmpleado': 'fecha_de_alta',
  'nominas.NominaPlaza': FECHA_FINAL_NOMINA,
  'nominas.NominaPlazaPercepcion': FECHA_FINAL_NOMINA,
  'nominas.NominaPlazaDeduccion': FECHA_FINAL_NOMINA,
  'nominas.NominaPlazaDescuento': FECHA_FINAL_NOMINA,
  'nominas.NominaPlazaIMSS': FECHA_FINAL_NOMINA,
  'nominas.NominaPlazaIPEJAL': FECHA_FINAL_NOMINA,
  'nominas.CalculoFuenteFinanciamiento': FECHA_FINAL_NOMINA,
  'nominas.Nomina': 'fecha_inicia_calculos',
  'relojchecador.CargaDeReporteDetalle': 'fecha',
  'relojchecador.CargaDeReporte': 'fecha_carga',
  'relojchecador.CargaDeReporteErrorAPI': 'fecha_de_creacion',
  'catalogos.AdministracionDePlaza': 'fecha_asignacion',
  'catalogos.HistorialSueldoPlaza': 'fecha_inicial',
  'catalogos.AutorizacionPlazaDetalle': 'fecha_autorizacion',
  'catalogos.HistoricoDeSindicato': 'fecha_inicial',
  'nominas.IncidenciaEmpleadoDetalle': 'fecha_inicial',
  'entidad.HistoricoSBCEmpleado': 'fecha_inicial',
  'nominas.SolicitudPercepcionEmpleado': 'solicitud_percepcion__fecha',
  'nominas.RegistroDiasGuardia': 'fecha_registro',
  'nominas.DescuentoCargaExternaArchivoEmpleado': 'descuento_carga_externa_archivo__fecha_de_carga',
  'catalogos.ErrorCargarPlantilla': 'fecha_de_creacion',
  'nominas.DescuentoEmpleado': 'fecha_de_asignacion',
};

const VIGENTES = ['Activo', 'Reingreso', 'Suspendido'];

// ---------- Diccionario 1: qué se mide ----------
// campos: [ancla o '*', selector, agregación por defecto]. Selectores: 'x' exacto,
// '~x' termina en x, '^x' empieza con x.
export const METRICAS = [
  {
    id: 'personas',
    grupo: 'conteo',
    etiqueta: 'Número de empleados',
    frases: ['cuantos empleados', 'cuantas personas', 'numero de empleados', 'numero de personas',
      'cantidad de empleados', 'cantidad de personas', 'total de empleados', 'empleados', 'empleado',
      'personas', 'personal', 'plantilla laboral', 'trabajadores', 'servidores publicos',
      'colaboradores', 'gente'],
    campos: [['entidad.Empleado', 'id', 'COUNT'], ['catalogos.DatoOperacionalDelEmpleado', 'id', 'COUNT'],
      ['*', 'empleado', 'DISTINCT'], ['*', 'nomina_plaza__empleado', 'DISTINCT']],
  },
  {
    id: 'altas',
    grupo: 'conteo',
    etiqueta: 'Altas (contrataciones)',
    frases: ['altas', 'contrataciones', 'ingresos de personal', 'nuevos ingresos'],
    campos: [['entidad.Empleado', 'id', 'COUNT']],
    fecha: { 'entidad.Empleado': `${DO}fecha_de_alta` },
  },
  {
    id: 'bajas',
    grupo: 'conteo',
    etiqueta: 'Bajas',
    frases: ['bajas definitivas', 'bajas', 'dados de baja', 'renuncias'],
    campos: [['entidad.Empleado', 'id', 'COUNT'], ['catalogos.AdministracionDePlaza', 'id', 'COUNT']],
    fecha: { 'entidad.Empleado': `${DO}fecha_de_baja`, 'catalogos.AdministracionDePlaza': 'fecha_desasignacion' },
    filtros: ['f_bajas'],
  },
  {
    id: 'reingresos',
    grupo: 'conteo',
    etiqueta: 'Reingresos',
    frases: ['reingresos'],
    campos: [['entidad.Empleado', 'id', 'COUNT']],
    fecha: { 'entidad.Empleado': `${DO}fecha_de_reingreso` },
  },
  {
    id: 'plazas',
    grupo: 'conteo',
    etiqueta: 'Número de plazas',
    frases: ['cuantas plazas', 'numero de plazas', 'plazas'],
    campos: [['catalogos.Plaza', 'id', 'COUNT'], ['catalogos.PlazaCosto', 'id', 'COUNT'], ['*', 'plaza', 'DISTINCT']],
  },
  {
    id: 'asignaciones',
    grupo: 'conteo',
    etiqueta: 'Asignaciones de plaza',
    frases: ['asignaciones de plaza', 'asignaciones', 'movimientos de plaza'],
    campos: [['catalogos.AdministracionDePlaza', 'id', 'COUNT'],
      ['catalogos.AutorizacionPlazaDetalle', 'administracion_plaza', 'DISTINCT']],
  },
  {
    id: 'desasignaciones',
    grupo: 'conteo',
    etiqueta: 'Desasignaciones de plaza',
    frases: ['desasignaciones'],
    campos: [['catalogos.AdministracionDePlaza', 'id', 'COUNT']],
    fecha: { 'catalogos.AdministracionDePlaza': 'fecha_desasignacion' },
    filtros: ['f_desasignadas'],
  },
  {
    id: 'cambios_sueldo',
    grupo: 'conteo',
    etiqueta: 'Cambios de sueldo',
    frases: ['cambios de sueldo', 'aumentos de sueldo', 'aumentos'],
    campos: [['catalogos.HistorialSueldoPlaza', 'id', 'COUNT']],
  },
  {
    id: 'autorizaciones',
    grupo: 'conteo',
    etiqueta: 'Autorizaciones de asignación',
    frases: ['autorizaciones'],
    campos: [['catalogos.AutorizacionPlazaDetalle', 'id', 'COUNT']],
  },
  {
    id: 'afiliaciones',
    grupo: 'conteo',
    etiqueta: 'Afiliaciones a sindicato',
    frases: ['afiliaciones', 'afiliados'],
    campos: [['catalogos.HistoricoDeSindicato', 'id', 'COUNT']],
  },
  {
    id: 'neto',
    grupo: 'neto',
    etiqueta: 'Importe neto pagado',
    frases: ['importe neto', 'neto pagado', 'pago neto', 'cuanto se pago', 'lo que se pago', 'lo pagado', 'neto', 'pagado'],
    campos: [['nominas.NominaPlaza', 'importe_neto', 'SUM'], ['nominas.Nomina', 'importe_neto', 'SUM']],
  },
  {
    id: 'percepciones',
    grupo: 'percepciones',
    etiqueta: 'Importe de percepciones (costo bruto)',
    frases: ['costo de nomina', 'costo de la nomina', 'gasto en nomina', 'gasto de nomina', 'cuanto cuesta',
      'cuanto cuestan', 'costo', 'gasto', 'importe de percepciones', 'percepciones', 'bruto'],
    campos: [['nominas.NominaPlaza', 'importe_percepciones', 'SUM'], ['nominas.Nomina', 'importe_percepciones', 'SUM'],
      ['nominas.NominaPlazaPercepcion', 'importe', 'SUM'], ['nominas.CalculoFuenteFinanciamiento', 'importe', 'SUM']],
  },
  {
    id: 'deducciones',
    grupo: 'deducciones',
    etiqueta: 'Importe de deducciones',
    frases: ['importe de deducciones', 'deducciones', 'deducido'],
    campos: [['nominas.NominaPlaza', 'importe_deducciones', 'SUM'], ['nominas.Nomina', 'importe_deducciones', 'SUM'],
      ['nominas.NominaPlazaDeduccion', 'importe', 'SUM']],
  },
  {
    id: 'descuentos',
    grupo: 'descuentos',
    etiqueta: 'Importe de descuentos',
    frases: ['importe de descuentos', 'importe descontado', 'descuentos', 'descontado', 'prestamos'],
    campos: [['nominas.NominaPlaza', 'importe_descuentos', 'SUM'], ['nominas.NominaPlazaDescuento', 'importe_descuento', 'SUM'],
      ['nominas.Nomina', 'importe_descuentos', 'SUM'], ['nominas.DescuentoEmpleado', 'importe_fijo', 'SUM']],
  },
  {
    id: 'isr',
    grupo: 'isr',
    etiqueta: 'ISR',
    frases: ['impuesto sobre la renta', 'isr retenido', 'isr'],
    campos: [['nominas.NominaPlaza', 'importe_isr', 'SUM'], ['nominas.Nomina', 'importe_isr', 'SUM']],
  },
  {
    id: 'isn',
    grupo: 'isn',
    etiqueta: 'ISN (impuesto sobre nómina)',
    frases: ['impuesto sobre nomina', 'impuesto estatal', 'isn'],
    campos: [['nominas.NominaPlaza', 'importe_isn', 'SUM'], ['nominas.Nomina', 'importe_isn', 'SUM']],
  },
  {
    id: 'imss_patron',
    grupo: 'imss',
    etiqueta: 'Cuota IMSS patronal',
    frases: ['cuotas patronales', 'cuota patronal', 'imss patronal', 'aportacion patronal', 'aportaciones patronales'],
    campos: [['nominas.NominaPlazaIMSS', 'importe_patron', 'SUM']],
  },
  {
    id: 'imss_trabajador',
    grupo: 'imss',
    etiqueta: 'Cuota IMSS del trabajador',
    frases: ['cuota del trabajador', 'cuotas del trabajador', 'imss del trabajador', 'cuota obrera'],
    campos: [['nominas.NominaPlazaIMSS', 'importe_trabajador', 'SUM']],
  },
  {
    id: 'imss',
    grupo: 'imss',
    etiqueta: 'Cuota IMSS total',
    frases: ['cuotas imss', 'cuotas del imss', 'seguro social', 'imss'],
    campos: [['nominas.NominaPlazaIMSS', 'total', 'SUM']],
  },
  {
    id: 'ipejal',
    grupo: 'ipejal',
    etiqueta: 'Aportación IPEJAL',
    frases: ['aportaciones ipejal', 'aportacion ipejal', 'ipejal', 'pensiones'],
    campos: [['nominas.NominaPlazaIPEJAL', 'importe', 'SUM']],
  },
  {
    id: 'costo_plantilla',
    grupo: 'sueldo',
    etiqueta: 'Costo mensual de plantilla (sueldo asignado)',
    frases: ['costo de la plantilla', 'costo de plantilla', 'costo mensual de plantilla', 'costo de plazas',
      'sueldo asignado', 'sueldos asignados'],
    campos: [['catalogos.AdministracionDePlaza', 'sueldo_mensual_nominal_asignado', 'SUM'],
      ['catalogos.Plaza', 'sueldo_mensual_nominal_asignado', 'SUM'],
      ['entidad.Empleado', `${DO}plaza__sueldo_mensual_nominal_asignado`, 'SUM']],
    filtros: ['f_autorizado_ap'],
  },
  {
    id: 'costo_plaza',
    grupo: 'costo_plaza',
    etiqueta: 'Costo de plaza (despensa)',
    frases: ['costo de despensa', 'despensa'],
    campos: [['catalogos.PlazaCosto', 'importe_mensual', 'SUM']],
  },
  {
    id: 'sueldo_diario',
    grupo: 'sueldo',
    etiqueta: 'Sueldo diario',
    frases: ['sueldo diario', 'salario diario'],
    campos: [['entidad.Empleado', `${DO}sueldo_diario`, 'AVG'],
      ['catalogos.AdministracionDePlaza', 'sueldo_diario_nominal_asignado', 'AVG'],
      ['catalogos.HistorialSueldoPlaza', 'sueldo_diario', 'AVG'],
      ['catalogos.Plaza', 'sueldo_diario_nominal_asignado', 'AVG']],
  },
  {
    id: 'sueldo',
    grupo: 'sueldo',
    etiqueta: 'Sueldo mensual',
    frases: ['sueldo mensual', 'sueldos', 'sueldo', 'salarios', 'salario'],
    campos: [['entidad.Empleado', `${DO}sueldo_mensual`, 'AVG'], ['nominas.NominaPlaza', 'sueldo_mensual', 'AVG'],
      ['catalogos.AdministracionDePlaza', 'sueldo_mensual_nominal_asignado', 'AVG'],
      ['catalogos.Plaza', 'sueldo_mensual_nominal_asignado', 'AVG'], ['catalogos.HistorialSueldoPlaza', 'sueldo_mensual', 'AVG']],
  },
  {
    id: 'sbc',
    grupo: 'sbc',
    etiqueta: 'Salario base de cotización',
    frases: ['salario base de cotizacion', 'salario base', 'sbc'],
    campos: [['entidad.Empleado', 'salario_base_cotizacion_imss', 'AVG'],
      ['entidad.HistoricoSBCEmpleado', 'salario_base_cotizacion_imss', 'AVG'],
      ['catalogos.AdministracionDePlaza', 'salario_base_cotizacion_imss', 'AVG'],
      ['nominas.NominaPlaza', 'importe_imss_sbc', 'AVG'], ['nominas.NominaPlazaIMSS', 'sbc', 'AVG']],
  },
  {
    id: 'edad',
    grupo: 'edad',
    etiqueta: 'Edad',
    frases: ['edad promedio', 'edades', 'edad'],
    campos: [['entidad.Empleado', 'edad', 'AVG'], ['nominas.NominaPlaza', 'edad', 'AVG']],
  },
  {
    id: 'antiguedad',
    grupo: 'antiguedad',
    etiqueta: 'Antigüedad (años)',
    frases: ['anos de servicio', 'antiguedad'],
    campos: [['entidad.Empleado', `${DO}antiguedad_numerico_anos`, 'AVG'],
      ['catalogos.DatoOperacionalDelEmpleado', 'antiguedad_numerico_anos', 'AVG'],
      ['nominas.SolicitudPercepcionEmpleado', 'antiguedad_numerico_anos', 'AVG']],
  },
  {
    id: 'dias_guardia',
    grupo: 'dias',
    etiqueta: 'Días de guardia',
    frases: ['dias de guardia', 'guardias'],
    campos: [['nominas.RegistroDiasGuardia', 'numero_dias', 'SUM']],
  },
  {
    id: 'dias_trabajados',
    grupo: 'dias',
    etiqueta: 'Días trabajados',
    frases: ['dias trabajados'],
    campos: [['nominas.NominaPlaza', 'dias_trabajados', 'SUM']],
  },
  {
    id: 'dias_50',
    grupo: 'dias',
    etiqueta: 'Días pagados al 50% (incapacidad)',
    frases: ['dias de incapacidad', 'dias al 50', 'dias pagados al 50'],
    campos: [['nominas.IncidenciaEmpleadoDetalle', 'dias_aplicados_al_50', 'SUM']],
  },
  {
    id: 'dias_0',
    grupo: 'dias',
    etiqueta: 'Días sin goce de sueldo',
    frases: ['dias sin goce', 'sin goce de sueldo', 'dias al 0'],
    campos: [['nominas.IncidenciaEmpleadoDetalle', 'dias_aplicados_al_0', 'SUM']],
  },
  {
    id: 'minutos',
    grupo: 'minutos',
    etiqueta: 'Minutos de incidencia',
    frases: ['minutos de retardo', 'minutos de incidencia', 'minutos'],
    campos: [['relojchecador.CargaDeReporteDetalle', 'minutos_de_incidencia', 'SUM']],
  },
  {
    id: 'incidencias',
    grupo: 'conteo',
    etiqueta: 'Registros de incidencia',
    frases: ['incidencias', 'ausencias', 'ausentismo'],
    campos: [['nominas.IncidenciaEmpleadoDetalle', 'id', 'COUNT']],
  },
  {
    id: 'checadas',
    grupo: 'conteo',
    etiqueta: 'Registros de asistencia',
    frases: ['registros de asistencia', 'checadas', 'asistencias', 'asistencia'],
    campos: [['relojchecador.CargaDeReporteDetalle', 'id', 'COUNT']],
  },
  {
    id: 'importe_solicitado',
    grupo: 'solicitado',
    etiqueta: 'Importe solicitado',
    frases: ['importe solicitado', 'monto solicitado'],
    campos: [['nominas.SolicitudPercepcionEmpleado', 'importe_de_solicitud', 'SUM']],
  },
  {
    id: 'solicitudes',
    grupo: 'conteo',
    etiqueta: 'Solicitudes de percepción',
    frases: ['solicitudes de percepcion', 'solicitudes'],
    campos: [['nominas.SolicitudPercepcionEmpleado', 'id', 'COUNT']],
  },
  {
    id: 'nominas',
    grupo: 'conteo',
    etiqueta: 'Número de nóminas',
    frases: ['nominas calculadas', 'cuantas nominas', 'numero de nominas', 'nominas', 'recalculos'],
    campos: [['nominas.Nomina', 'id', 'COUNT']],
  },
  {
    id: 'cargas_externas',
    grupo: 'externas',
    etiqueta: 'Importe de cargas externas',
    frases: ['cargas externas', 'carga externa', 'importe de proveedores'],
    campos: [['nominas.DescuentoCargaExternaArchivoEmpleado', 'importe', 'SUM']],
  },
  {
    id: 'errores_importacion',
    grupo: 'conteo',
    etiqueta: 'Errores de importación',
    frases: ['errores de importacion', 'errores al importar', 'errores de plantillas'],
    campos: [['catalogos.ErrorCargarPlantilla', 'id', 'COUNT']],
  },
  {
    id: 'cargas_reloj',
    grupo: 'conteo',
    etiqueta: 'Cargas del reloj',
    frases: ['cargas del reloj', 'cargas de reloj'],
    campos: [['relojchecador.CargaDeReporte', 'id', 'COUNT']],
  },
  {
    id: 'sueldo_tabular',
    grupo: 'tabular',
    etiqueta: 'Sueldo tabular',
    frases: ['sueldo tabular', 'tabular'],
    campos: [['nominas.NominaPlaza', 'sueldo_tabular_nominal', 'AVG']],
  },
  {
    id: 'isr_retenido',
    grupo: 'isr',
    etiqueta: 'ISR retenido',
    frases: ['isr retenido', 'retencion de isr'],
    campos: [['nominas.NominaPlaza', 'importe_isr_retenido', 'SUM']],
  },
  {
    id: 'neto_dispersion',
    grupo: 'dispersion',
    etiqueta: 'Neto por dispersión bancaria',
    frases: ['neto por dispersion', 'dispersion bancaria', 'dispersion', 'transferencia'],
    campos: [['nominas.NominaPlaza', 'importe_neto_dispersion_bancaria', 'SUM']],
  },
  {
    id: 'neto_monedero',
    grupo: 'monedero',
    etiqueta: 'Neto por monedero',
    frases: ['neto por monedero', 'monedero', 'vales'],
    campos: [['nominas.NominaPlaza', 'importe_neto_monedero', 'SUM']],
  },
  {
    id: 'base_isn',
    grupo: 'base_isn',
    etiqueta: 'Base gravable ISN',
    frases: ['base gravable isn', 'base de isn'],
    campos: [['nominas.NominaPlaza', 'importe_isn_base_gravable', 'SUM']],
  },
  {
    id: 'base_isr',
    grupo: 'base_isr',
    etiqueta: 'Base gravable ISR',
    frases: ['base gravable isr', 'base de isr', 'base gravable'],
    campos: [['nominas.NominaPlaza', 'importe_isr_base_gravable', 'SUM']],
  },
  {
    id: 'subsidio',
    grupo: 'subsidio',
    etiqueta: 'Subsidio para el empleo',
    frases: ['subsidio para el empleo', 'subsidio'],
    campos: [['nominas.NominaPlaza', 'importe_subsidio_causado_total', 'SUM']],
  },
  {
    id: 'base_ipejal',
    grupo: 'base_ipejal',
    etiqueta: 'Base de cálculo IPEJAL',
    frases: ['base de calculo ipejal', 'base ipejal'],
    campos: [['nominas.NominaPlazaIPEJAL', 'base_gravable', 'SUM']],
  },
  {
    id: 'isn_gravado',
    grupo: 'isn_gravado',
    etiqueta: 'Percepción gravada (ISN)',
    frases: ['isn gravado'],
    campos: [['nominas.NominaPlazaPercepcion', 'importe_isn_gravado', 'SUM']],
  },
  {
    id: 'isn_exento',
    grupo: 'isn_exento',
    etiqueta: 'Percepción exenta (ISN)',
    frases: ['isn exento'],
    campos: [['nominas.NominaPlazaPercepcion', 'importe_isn_excento', 'SUM']],
  },
  {
    id: 'gravado',
    grupo: 'gravado',
    etiqueta: 'Percepción gravada (ISR)',
    frases: ['importe gravado', 'isr gravado', 'gravado', 'gravada'],
    campos: [['nominas.NominaPlazaPercepcion', 'importe_isr_gravado', 'SUM']],
  },
  {
    id: 'exento',
    grupo: 'exento',
    etiqueta: 'Percepción exenta (ISR)',
    frases: ['importe exento', 'isr exento', 'exento', 'exenta'],
    campos: [['nominas.NominaPlazaPercepcion', 'importe_isr_excento', 'SUM']],
  },
  {
    id: 'dias_100',
    grupo: 'dias',
    etiqueta: 'Días pagados al 100%',
    frases: ['dias pagados al 100', 'dias al 100', 'dias con goce'],
    campos: [['nominas.IncidenciaEmpleadoDetalle', 'dias_aplicados_al_100', 'SUM']],
  },
  {
    id: 'cantidad_incidencia',
    grupo: 'cantidad',
    etiqueta: 'Cantidad de la incidencia',
    frases: ['cantidad de la incidencia', 'horas de permiso'],
    campos: [['nominas.IncidenciaEmpleadoDetalle', 'cantidad', 'SUM']],
  },
  {
    id: 'vacaciones',
    grupo: 'vacaciones',
    etiqueta: 'Días de vacaciones pendientes',
    frases: ['dias de vacaciones', 'vacaciones pendientes', 'vacaciones'],
    campos: [['entidad.Empleado', `${DO}dias_habiles_pendientes`, 'SUM']],
  },
  {
    id: 'archivos',
    grupo: 'archivos',
    etiqueta: 'Archivos cargados',
    frases: ['archivos cargados', 'archivos'],
    campos: [['nominas.DescuentoCargaExternaArchivoEmpleado', 'descuento_carga_externa_archivo', 'DISTINCT']],
  },
  {
    id: 'registros_recibidos',
    grupo: 'recibidos',
    etiqueta: 'Registros recibidos del reloj',
    frases: ['registros recibidos', 'checadas recibidas'],
    campos: [['relojchecador.CargaDeReporte', 'total_registros', 'SUM']],
  },
  {
    id: 'errores_api',
    grupo: 'conteo',
    etiqueta: 'Errores de la API del reloj',
    frases: ['errores de la api', 'errores del reloj'],
    campos: [['relojchecador.CargaDeReporteErrorAPI', 'id', 'COUNT']],
  },
];

// ---------- Diccionario 2: por qué se agrupa (lo que va después de "por") ----------
// tipo 'num': número agrupado en rangos de `ancho`. pocos: pocas categorías (pastel);
// muchas: muchas categorías (barras horizontales).
export const DIMENSIONES = [
  {
    id: 'dependencia',
    etiqueta: 'Dependencia',
    frases: ['dependencias', 'dependencia', 'direcciones', 'direccion', 'secretarias', 'secretaria',
      'coordinaciones', 'coordinacion'],
    sel: ['^dependencia:'],
    muchas: true,
  },
  {
    id: 'area_superior', etiqueta: 'Área superior (un nivel arriba)', frases: ['area superior', 'areas superiores'], sel: ['~directorio_area_padre__nombre'], muchas: true,
  },
  {
    id: 'area', etiqueta: 'Área', frases: ['areas', 'area', 'departamentos', 'departamento'], sel: ['~directorio_de_area__nombre'], muchas: true,
  },
  {
    id: 'puesto', etiqueta: 'Puesto', frases: ['puestos', 'puesto', 'cargos', 'cargo'], sel: ['~puesto__descripcion', '~puesto__clave_y_descripcion'], muchas: true, limite: 15,
  },
  {
    id: 'genero', etiqueta: 'Género', frases: ['genero', 'sexo'], sel: ['~genero__descripcion'], pocos: true,
  },
  {
    id: 'estado_civil', etiqueta: 'Estado civil', frases: ['estado civil'], sel: ['~estado_civil__descripcion'], pocos: true,
  },
  {
    id: 'nivel_estudios', etiqueta: 'Nivel de estudios', frases: ['nivel de estudios', 'escolaridad', 'nivel educativo'], sel: ['~nivel_de_estudios__nombre'],
  },
  {
    id: 'tipo_presupuestal',
    etiqueta: 'Tipo de empleado presupuestal',
    frases: ['tipo de empleado presupuestal', 'tipo presupuestal', 'tipos presupuestales'],
    sel: ['~tipo_de_empleado_presupuestal__descripcion', 'tipo_empleado_presupuestal__descripcion'],
    pocos: true,
  },
  {
    id: 'tipo_empleado', etiqueta: 'Tipo de empleado', frases: ['tipo de empleado', 'tipos de empleado', 'tipo de personal'], sel: ['~tipo_empleado__descripcion'],
  },
  {
    id: 'tipo_contratacion', etiqueta: 'Tipo de contratación', frases: ['tipo de contratacion', 'contratacion'], sel: ['~tipo_de_contratacion__descripcion'],
  },
  {
    id: 'clase_contratacion', etiqueta: 'Clase de contratación', frases: ['clase de contratacion'], sel: ['~clase_de_contratacion__descripcion'],
  },
  {
    id: 'jornada', etiqueta: 'Jornada', frases: ['jornada', 'jornadas', 'turno', 'turnos'], sel: ['~jornada__descripcion'], pocos: true,
  },
  {
    id: 'sindicato', etiqueta: 'Sindicato', frases: ['sindicatos', 'sindicato'], sel: ['~sindicato__descripcion'], pocos: true,
  },
  {
    id: 'sindicalizado', etiqueta: 'Sindicalizado (Sí / No)', frases: ['sindicalizado', 'sindicalizados'], sel: ['~sindicalizado'], pocos: true,
  },
  {
    id: 'tipo_checado', etiqueta: 'Tipo de checado', frases: ['tipo de checado', 'tipos de checado', 'checado'], sel: ['~tipo_de_checado__nombre'], pocos: true,
  },
  {
    id: 'categoria', etiqueta: 'Categoría de puesto', frases: ['categoria', 'categorias'], sel: ['~categoria__nombre'], pocos: true,
  },
  {
    id: 'tipo_plaza', etiqueta: 'Tipo de plaza', frases: ['tipo de plaza', 'tipos de plaza'], sel: ['~tipo_de_plaza__descripcion'], pocos: true,
  },
  {
    id: 'estado_plaza',
    etiqueta: 'Estado de la plaza',
    frases: ['estado de la plaza', 'estado de plaza', 'situacion de la plaza'],
    sel: ['~estado_plaza__descripcion'],
    pocos: true,
  },
  {
    id: 'nomina_empleado',
    etiqueta: 'Nómina de empleado (plantilla / eventuales)',
    frases: ['nomina de empleado', 'tipo de nomina de empleado', 'plantilla y eventuales'],
    sel: ['~tipo_de_nomina_empleado__descripcion'],
    pocos: true,
  },
  {
    id: 'tipo_nomina',
    etiqueta: 'Tipo de nómina',
    frases: ['tipo de nomina', 'tipos de nomina'],
    sel: ['tipo_de_nomina__descripcion', 'nomina__tipo_de_nomina__descripcion'],
    pocos: true,
  },
  {
    id: 'clave_percepcion', etiqueta: 'Clave de percepción', frases: ['clave de percepcion'], sel: ['~percepcion__clave'], muchas: true,
  },
  {
    id: 'percepcion', etiqueta: 'Percepción', frases: ['concepto de percepcion', 'percepciones', 'percepcion'], sel: ['percepcion__descripcion', 'solicitud_percepcion__solicitud__percepcion__descripcion'], muchas: true, limite: 10,
  },
  {
    id: 'deduccion', etiqueta: 'Deducción', frases: ['deducciones', 'deduccion'], sel: ['~deduccion__concepto'],
  },
  {
    id: 'descuento', etiqueta: 'Descuento', frases: ['tipo de descuento', 'descuentos', 'descuento'], sel: ['~descuento__clave_y_concepto'], muchas: true, limite: 10,
  },
  {
    // "por concepto": se resuelve según lo que se mida.
    id: 'concepto',
    etiqueta: 'Concepto',
    frases: ['conceptos', 'concepto'],
    sel: ['~descuento__clave_y_concepto', 'percepcion__descripcion', '~deduccion__concepto', '~incidencia__concepto',
      'descripcion_del_concepto_externo', '~concepto_ipejal__concepto'],
    muchas: true,
    limite: 10,
  },
  {
    id: 'banco', etiqueta: 'Banco', frases: ['bancos', 'banco'], sel: ['~banco__nombre'], pocos: true,
  },
  {
    id: 'metodo_pago',
    etiqueta: 'Método / forma de pago',
    frases: ['metodo de pago', 'forma de pago', 'formas de pago'],
    sel: ['~metodo_de_pago__concepto', '~forma_de_pago__concepto', '~forma_pago__concepto'],
    pocos: true,
  },
  {
    id: 'tipo_deposito', etiqueta: 'Tipo de depósito', frases: ['tipo de deposito', 'deposito'], sel: ['~tipos_de_depositos__concepto'], pocos: true,
  },
  {
    id: 'fuente',
    etiqueta: 'Fuente de financiamiento',
    frases: ['fuente de financiamiento', 'fuentes de financiamiento', 'fuentes', 'fuente'],
    sel: ['~fuente_financiamiento__nombre'],
    pocos: true,
  },
  {
    id: 'cog', etiqueta: 'Objeto del gasto (COG)', frases: ['objeto del gasto', 'objeto de gasto', 'cog', 'partida'], sel: ['~cog__concepto'],
  },
  {
    id: 'tipo_gasto', etiqueta: 'Tipo de gasto', frases: ['tipo de gasto'], sel: ['~tipo_de_gasto__nombre'], pocos: true,
  },
  {
    id: 'proyecto', etiqueta: 'Proyecto presupuestal', frases: ['proyecto presupuestal', 'proyecto'], sel: ['~proyecto_presupuestal__descripcion'], pocos: true,
  },
  {
    id: 'destino', etiqueta: 'Destino presupuestal', frases: ['destino presupuestal'], sel: ['~destino_presupuestal__nombre'],
  },
  {
    id: 'seguridad_social', etiqueta: 'Seguridad social', frases: ['seguridad social'], sel: ['~tipo_de_seguridad_social__concepto'], pocos: true,
  },
  {
    id: 'registro_patronal', etiqueta: 'Registro patronal', frases: ['registro patronal'], sel: ['~registro_patronal__numero_registro_patronal'], pocos: true,
  },
  {
    id: 'riesgo', etiqueta: 'Riesgo de puesto', frases: ['riesgo de puesto', 'riesgo'], sel: ['~riesgo_puesto__descripcion'], pocos: true,
  },
  {
    id: 'periodo',
    etiqueta: 'Periodo de nómina',
    frases: ['periodo de nomina', 'periodos', 'periodo', 'quincenas', 'quincena'],
    sel: ['~numero_de_nomina'],
    orden: 'etiqueta_asc',
    serie: true,
  },
  {
    id: 'ejercicio', etiqueta: 'Ejercicio', frases: ['ejercicio', 'ejercicios'], sel: ['~periodo_fiscal__periodo'], orden: 'etiqueta_asc', serie: true,
  },
  {
    id: 'estado_empleado',
    etiqueta: 'Estado del empleado',
    frases: ['estado del empleado', 'estatus del empleado', 'situacion del empleado'],
    sel: ['~estado_del_empleado__descripcion'],
    pocos: true,
  },
  {
    id: 'motivo_baja', etiqueta: 'Motivo de baja', frases: ['motivo de baja', 'motivos de baja'], sel: ['~motivo_baja__concepto'],
  },
  {
    id: 'motivo_alta', etiqueta: 'Motivo de alta', frases: ['motivo de alta', 'motivos de alta'], sel: ['~motivo_alta__concepto'],
  },
  {
    id: 'motivo_desasignacion',
    etiqueta: 'Motivo de desasignación',
    frases: ['motivo de desasignacion', 'motivos de desasignacion'],
    sel: ['~motivo_desasignacion__concepto'],
    limite: 10,
  },
  {
    id: 'motivo_asignacion',
    etiqueta: 'Motivo de asignación',
    frases: ['motivo de asignacion', 'motivos de asignacion'],
    sel: ['~motivo_asignacion__concepto'],
    limite: 10,
  },
  {
    id: 'tipo_incidencia',
    etiqueta: 'Tipo de incidencia',
    frases: ['tipo de incidencia', 'tipos de incidencia'],
    sel: ['~incidencia__tipo_de_incidencia__descripcion'],
    pocos: true,
  },
  {
    id: 'incidencia',
    etiqueta: 'Incidencia (concepto)',
    frases: ['concepto de incidencia', 'incidencias', 'incidencia'],
    sel: ['~incidencia__concepto'],
    muchas: true,
    limite: 10,
  },
  {
    id: 'evaluacion_entrada',
    etiqueta: 'Evaluación de entrada (puntualidad)',
    frases: ['evaluacion de entrada', 'puntualidad', 'tipo de entrada'],
    sel: ['evaluacion_entrada__nombre'],
    pocos: true,
    filtroValores: ['PUNTUAL', 'TOLERANCIA', 'RETARDO', 'OMISIÓN DE ENTRADA', 'INCIDENCIA'],
  },
  {
    id: 'evaluacion_salida',
    etiqueta: 'Evaluación de salida',
    frases: ['evaluacion de salida', 'tipo de salida'],
    sel: ['evaluacion_salida__nombre'],
    pocos: true,
    filtroValores: ['OMISIÓN DE SALIDA', 'SALIDA ANTICIPADA', 'RETARDO'],
  },
  {
    id: 'dia_semana', etiqueta: 'Día de la semana', frases: ['dia de la semana', 'dias de la semana'], sel: ['~dia_de_la_semana__nombre'], orden: 'etiqueta_asc',
  },
  {
    id: 'estado_nacimiento',
    etiqueta: 'Estado de nacimiento',
    frases: ['estado de nacimiento', 'lugar de nacimiento', 'entidad de nacimiento'],
    sel: ['lugar_de_nacimiento__nom_ent'],
  },
  {
    id: 'codigo_postal', etiqueta: 'Código postal', frases: ['codigo postal', 'codigos postales'], sel: ['~codigo_postal__d_codigo'], limite: 10,
  },
  {
    id: 'proveedor', etiqueta: 'Proveedor / carga', frases: ['proveedores', 'proveedor'], sel: ['~concepto_carga_externa__nombre'], pocos: true,
  },
  {
    id: 'concepto_externo',
    etiqueta: 'Concepto externo',
    frases: ['concepto externo', 'conceptos externos'],
    sel: ['descripcion_del_concepto_externo'],
    limite: 10,
  },
  {
    id: 'concepto_ipejal', etiqueta: 'Concepto IPEJAL', frases: ['concepto ipejal', 'conceptos ipejal'], sel: ['~concepto_ipejal__concepto'], pocos: true,
  },
  {
    id: 'codigo_error', etiqueta: 'Código de error de la API', frases: ['codigo de error', 'codigos de error'], sel: ['no_error'], pocos: true,
  },
  {
    id: 'tipo_importacion', etiqueta: 'Tipo de importación', frases: ['tipo de importacion', 'tipo de plantilla'], sel: ['encabezado'], pocos: true,
  },
  {
    id: 'estado',
    etiqueta: 'Estado',
    frases: ['estado de la nomina', 'estado de la solicitud', 'estado de la asignacion', 'estatus', 'estados', 'estado'],
    sel: ['~estados_globales__descripcion'],
    pocos: true,
  },
  // Revisión de cobertura del catálogo (20/10/2026): campos que ninguna palabra alcanzaba.
  {
    id: 'tiene_foto',
    etiqueta: 'Tiene foto',
    frases: ['tiene foto', 'con foto', 'fotografia', 'fotos', 'foto'],
    sel: ['tiene_foto'],
    pocos: true,
  },
  {
    id: 'tiene_hijos',
    etiqueta: 'Tiene hijos',
    frases: ['tiene hijos', 'hijos'],
    sel: ['tiene_hijos'],
    pocos: true,
  },
  {
    id: 'estudios_concluidos',
    etiqueta: 'Estudios concluidos',
    frases: ['estudios concluidos', 'estudios terminados'],
    sel: ['estudios_concluidos'],
    pocos: true,
  },
  {
    id: 'comisionado',
    etiqueta: 'Comisionado en área externa',
    frases: ['comisionados', 'comisionado', 'comision'],
    sel: ['comisionado_en_area_externa'],
    pocos: true,
  },
  {
    id: 'area_externa',
    etiqueta: 'Área externa de comisión',
    frases: ['area externa', 'area de comision'],
    sel: ['area_externa_de_comision__nombre_del_area'],
  },
  {
    id: 'nacionalidad',
    etiqueta: 'Nacionalidad',
    frases: ['nacionalidad', 'nacionalidades'],
    sel: ['nacionalidad__descripcion'],
    pocos: true,
  },
  {
    id: 'pais',
    etiqueta: 'País',
    frases: ['pais', 'paises'],
    sel: ['pais__nombre'],
    pocos: true,
  },
  {
    id: 'municipio_nacimiento',
    etiqueta: 'Municipio de nacimiento',
    frases: ['municipio de nacimiento', 'municipio'],
    sel: ['lugar_de_nacimiento__nom_mun'],
    limite: 10,
  },
  {
    id: 'especialidad',
    etiqueta: 'Especialidad de estudio',
    frases: ['especialidad', 'carrera', 'profesion'],
    sel: ['especialidad_estudio__nombre'],
    limite: 10,
  },
  {
    id: 'tipo_identificacion',
    etiqueta: 'Tipo de identificación',
    frases: ['tipo de identificacion', 'identificacion'],
    sel: ['tipo_de_identificacion__nombre'],
    pocos: true,
  },
  {
    id: 'origen_contratacion',
    etiqueta: 'Origen de contratación',
    frases: ['origen de contratacion', 'origen'],
    sel: ['~tipo_de_origen_contratacion__descripcion'],
    pocos: true,
  },
  {
    id: 'tipo_movimiento',
    etiqueta: 'Tipo de movimiento',
    frases: ['tipo de movimiento', 'movimiento'],
    sel: ['tipo_de_movimiento__concepto'],
    pocos: true,
  },
  {
    id: 'nivel_jerarquico',
    etiqueta: 'Nivel jerárquico del área',
    frases: ['nivel jerarquico', 'nivel del area'],
    sel: ['~nivel_jerarquico__descripcion'],
    pocos: true,
  },
  {
    id: 'tipo_sueldo',
    etiqueta: 'Tipo de sueldo',
    frases: ['tipo de sueldo'],
    sel: ['~tipo_de_sueldo__descripcion'],
    pocos: true,
  },
  {
    id: 'periodicidad',
    etiqueta: 'Periodicidad de pago',
    frases: ['periodicidad de pago', 'periodicidad'],
    sel: ['~periodicidad_de_pago__descripcion'],
    pocos: true,
  },
  {
    id: 'regimen',
    etiqueta: 'Régimen de nómina',
    frases: ['regimen de nomina', 'regimen'],
    sel: ['~tipo_regimen_nomina__descripcion'],
    pocos: true,
  },
  {
    id: 'reloj_checador',
    etiqueta: 'Aplica reloj checador',
    frases: ['aplica reloj checador', 'reloj checador'],
    sel: ['~aplica_reloj_checador'],
    pocos: true,
  },
  {
    id: 'guardia',
    etiqueta: 'Aplica guardia',
    frases: ['aplica guardia', 'guardia'],
    sel: ['~aplica_guardia'],
    pocos: true,
  },
  {
    id: 'es_baja',
    etiqueta: 'Es baja definitiva',
    frases: ['baja definitiva', 'es baja'],
    sel: ['es_baja'],
    pocos: true,
  },
  {
    id: 'aplica_liquidacion',
    etiqueta: 'Aplica liquidación',
    frases: ['aplica liquidacion', 'liquidacion'],
    sel: ['aplica_liquidacion'],
    pocos: true,
  },
  {
    id: 'paso_autorizacion',
    etiqueta: 'Paso de autorización',
    frases: ['paso de autorizacion', 'paso'],
    sel: ['~configuracion_paso__descripcion'],
    pocos: true,
  },
  {
    id: 'retroactivo',
    etiqueta: 'Con retroactivo',
    frases: ['sueldo retroactivo', 'retroactivo'],
    sel: ['sueldo_retroactivo', 'aplica_pago_de_retroactivo'],
    pocos: true,
  },
  {
    id: 'afiliacion_vigente',
    etiqueta: 'Afiliación vigente',
    frases: ['afiliacion vigente'],
    sel: ['estatus'],
    pocos: true,
  },
  {
    id: 'disponible',
    etiqueta: 'Plaza disponible',
    frases: ['plaza disponible', 'disponibilidad', 'disponible'],
    sel: ['disponible'],
    pocos: true,
  },
  {
    id: 'suspension',
    etiqueta: 'Suspensión definitiva',
    frases: ['suspension definitiva', 'suspension'],
    sel: ['es_suspencion_definitiva'],
    pocos: true,
  },
  {
    id: 'unidad_incidencia',
    etiqueta: 'Unidad de la incidencia',
    frases: ['unidad de la incidencia', 'unidad'],
    sel: ['~tipo_de_incidencia_unidad__descripcion'],
    pocos: true,
  },
  {
    id: 'aplicada_nomina',
    etiqueta: 'Aplicada en nómina',
    frases: ['aplicada en nomina', 'calculada'],
    sel: ['calculada'],
    pocos: true,
  },
  {
    id: 'subtipo_nomina',
    etiqueta: 'Subtipo de nómina',
    frases: ['subtipo de nomina', 'subtipo'],
    sel: ['~subtipo_de_nomina__descripcion'],
    pocos: true,
  },
  {
    id: 'mes_nomina',
    etiqueta: 'Mes de nómina',
    frases: ['mes de nomina', 'mes del periodo', 'mes de pago'],
    sel: ['~periodo_de_nomina_detalle__mes'],
    orden: 'etiqueta_asc',
    serie: true,
  },
  {
    id: 'calculo_por_empleado',
    etiqueta: 'Cálculo por empleado',
    frases: ['calculo por empleado'],
    sel: ['aplica_calculo_por_empleado'],
    pocos: true,
  },
  {
    id: 'nomina_autorizada',
    etiqueta: 'Nómina autorizada',
    frases: ['nomina autorizada'],
    sel: ['nomina__nomina_autorizada'],
    pocos: true,
  },
  {
    id: 'plaza',
    etiqueta: 'Plaza',
    frases: ['plaza', 'plazas'],
    sel: ['plaza__clave_y_descripcion'],
    muchas: true,
    limite: 15,
  },
  {
    id: 'timbrado',
    etiqueta: 'Estatus de timbrado',
    frases: ['estatus de timbrado', 'timbrado'],
    sel: ['~tipo_de_estatus_timbrado__descripcion'],
    pocos: true,
  },
  {
    id: 'contrato_sat',
    etiqueta: 'Tipo de contrato SAT',
    frases: ['tipo de contrato sat', 'contrato sat', 'tipo de contrato'],
    sel: ['~tipo_de_contrato_sat__descripcion'],
    pocos: true,
  },
  {
    id: 'tipo_sat_deduccion',
    etiqueta: 'Tipo SAT de deducción',
    frases: ['tipo sat de deduccion', 'tipo de deduccion'],
    sel: ['~tipo_deduccion_nomina__descripcion'],
    pocos: true,
  },
  {
    id: 'tipo_sat_percepcion',
    etiqueta: 'Tipo SAT de percepción',
    frases: ['tipo sat de percepcion', 'tipo sat'],
    sel: ['~catsat_percepcion__descripcion'],
    pocos: true,
  },
  {
    id: 'es_sueldo',
    etiqueta: 'Es sueldo',
    frases: ['es sueldo'],
    sel: ['~es_sueldo'],
    pocos: true,
  },
  {
    id: 'es_recurrente',
    etiqueta: 'Es recurrente',
    frases: ['es recurrente', 'recurrente'],
    sel: ['~es_recurrente'],
    pocos: true,
  },
  {
    id: 'afecta_presupuesto',
    etiqueta: 'Afecta presupuesto',
    frases: ['afecta presupuesto'],
    sel: ['~afecta_presupuesto'],
    pocos: true,
  },
  {
    id: 'es_incidencia',
    etiqueta: 'Es incidencia',
    frases: ['es incidencia'],
    sel: ['~es_incidencia'],
    pocos: true,
  },
  {
    id: 'carga_exitosa',
    etiqueta: 'Carga exitosa',
    frases: ['carga exitosa', 'resultado de la carga', 'exitosa'],
    sel: ['carga_exitosa'],
    pocos: true,
  },
  {
    id: 'evaluacion_dia',
    etiqueta: 'Evaluación del día',
    frases: ['evaluacion del dia', 'tipo de dia'],
    sel: ['evaluacion_otros__nombre'],
    pocos: true,
  },
  {
    id: 'origen_registro',
    etiqueta: 'Origen del registro',
    frases: ['origen del registro', 'tipo de checador'],
    sel: ['tipo_checador'],
    pocos: true,
  },
  {
    id: 'mensaje_error',
    etiqueta: 'Mensaje de error',
    frases: ['mensaje de error', 'mensajes de error'],
    sel: ['error'],
    pocos: true,
  },
  {
    id: 'sueldo_diario_rango',
    etiqueta: 'Sueldo diario',
    frases: ['rango de sueldo diario', 'sueldo diario'],
    sel: [`${DO}sueldo_diario`],
    tipo: 'num',
    ancho: 100,
  },
  {
    id: 'percepciones_rango',
    etiqueta: 'Importe de percepciones (rangos)',
    frases: ['rango de percepciones', 'rangos de percepciones'],
    sel: ['importe_percepciones'],
    tipo: 'num',
    ancho: 5000,
  },
  {
    id: 'dias_trabajados_rango',
    etiqueta: 'Días trabajados',
    frases: ['dias trabajados'],
    sel: ['dias_trabajados'],
    tipo: 'num',
    ancho: 5,
  },
  {
    id: 'es_incapacidad',
    etiqueta: 'Es incapacidad médica',
    frases: ['es incapacidad', 'incapacidad medica'],
    sel: ['~es_una_incapacidad_medica'],
    pocos: true,
  },
  {
    id: 'es_permiso',
    etiqueta: 'Es permiso',
    frases: ['es permiso'],
    sel: ['~es_un_permiso'],
    pocos: true,
  },
  {
    id: 'periodicidad_percepcion',
    etiqueta: 'Periodicidad de la percepción',
    frases: ['periodicidad de la percepcion', 'periodicidad de percepcion'],
    sel: ['~periodicidad_de_percepcion__descripcion'],
    pocos: true,
  },
  // Números (se agrupan en rangos)
  {
    id: 'edad', etiqueta: 'Edad', frases: ['rangos de edad', 'rango de edad', 'edades', 'edad'], sel: ['edad'], tipo: 'num', ancho: 5,
  },
  {
    id: 'sueldo_rango',
    etiqueta: 'Sueldo mensual',
    frases: ['rangos de sueldo', 'rango de sueldo', 'rangos de salario', 'rango de salario', 'sueldos', 'sueldo', 'salario'],
    sel: [`${DO}sueldo_mensual`, 'sueldo_mensual', 'sueldo_mensual_nominal_asignado',
      `${DO}plaza__sueldo_mensual_nominal_asignado`],
    tipo: 'num',
    ancho: 5000,
  },
  {
    id: 'antiguedad_rango',
    etiqueta: 'Antigüedad (años)',
    frases: ['rangos de antiguedad', 'rango de antiguedad', 'antiguedad', 'anos de servicio'],
    sel: ['~antiguedad_numerico_anos'],
    tipo: 'num',
    ancho: 5,
  },
  {
    id: 'neto_rango',
    etiqueta: 'Importe neto (rangos)',
    frases: ['rangos de neto', 'rango de neto', 'rango de pago'],
    sel: ['importe_neto'],
    tipo: 'num',
    ancho: 5000,
  },
  {
    id: 'minutos_rango',
    etiqueta: 'Minutos de incidencia (rangos)',
    frases: ['rangos de minutos', 'rango de minutos', 'minutos'],
    sel: ['minutos_de_incidencia'],
    tipo: 'num',
    ancho: 10,
  },
  {
    id: 'sbc_rango',
    etiqueta: 'SBC (rangos)',
    frases: ['rangos de sbc', 'rango de sbc'],
    sel: ['salario_base_cotizacion_imss', 'sbc'],
    tipo: 'num',
    ancho: 250,
  },
];

const AGRUPACIONES_FECHA = [
  { valor: 'mes', etiqueta: 'Por mes', frases: ['por mes', 'mes a mes', 'cada mes', 'mensual', 'mensuales', 'mensualmente', 'por meses'] },
  { valor: 'anio', etiqueta: 'Por año', frases: ['por ano', 'por anos', 'cada ano', 'anual', 'anuales', 'ano con ano'] },
  { valor: 'dia', etiqueta: 'Por día', frases: ['por dia', 'por dias', 'diario', 'diaria', 'cada dia'] },
];

// ---------- Diccionario 3: filtros ----------
// opciones: [selector, operador, valor]; se usa la primera cuyo campo exista en la tabla elegida.
export const FILTROS = [
  {
    id: 'f_no_sindicalizados', etiqueta: 'No sindicalizados', frases: ['no sindicalizados', 'no sindicalizado', 'sin sindicato'], opciones: [['~sindicalizado', 'exact', false]],
  },
  {
    id: 'f_sindicalizados', etiqueta: 'Sindicalizados', frases: ['sindicalizados', 'sindicalizado', 'con sindicato'], opciones: [['~sindicalizado', 'exact', true]],
  },
  {
    id: 'f_vigentes',
    etiqueta: 'Personal vigente',
    frases: ['vigentes', 'vigente', 'activos', 'activas', 'actuales', 'en activo'],
    opciones: [['estado_del_empleado__descripcion', 'in', VIGENTES]],
  },
  {
    id: 'f_bajas',
    etiqueta: 'Solo bajas',
    frases: ['dados de baja', 'de baja'],
    opciones: [['estado_del_empleado__descripcion', 'exact', 'Baja'], ['es_baja', 'exact', true]],
  },
  {
    id: 'f_mujeres', etiqueta: 'Mujeres', frases: ['mujeres', 'femenino', 'femenil'], opciones: [['~genero__descripcion', 'exact', 'Femenino']],
  },
  {
    id: 'f_hombres', etiqueta: 'Hombres', frases: ['hombres', 'masculino', 'varones'], opciones: [['~genero__descripcion', 'exact', 'Masculino']],
  },
  {
    id: 'f_faltas',
    etiqueta: 'Faltas injustificadas (de quienes checan)',
    frases: ['faltas injustificadas', 'faltas', 'inasistencias'],
    opciones: [['evaluacion_otros__nombre', 'exact', 'FALTA INJUSTIFICADA']],
    extra: [['~tipo_de_checado__nombre', 'in', ['CHECADO CON HUELLA', 'CHECADO CON RECONOCIMIENTO FACIAL']]],
  },
  {
    id: 'f_retardos', etiqueta: 'Retardos', frases: ['retardos', 'retardo', 'llegadas tarde'], opciones: [['evaluacion_entrada__nombre', 'exact', 'RETARDO']],
  },
  {
    id: 'f_puntuales', etiqueta: 'Llegadas puntuales', frases: ['puntuales', 'a tiempo'], opciones: [['evaluacion_entrada__nombre', 'exact', 'PUNTUAL']],
  },
  {
    id: 'f_omisiones_entrada',
    etiqueta: 'Omisiones de entrada',
    frases: ['omisiones de entrada', 'omision de entrada'],
    opciones: [['evaluacion_entrada__nombre', 'exact', 'OMISIÓN DE ENTRADA']],
  },
  {
    id: 'f_salidas',
    etiqueta: 'Omisiones y salidas anticipadas',
    frases: ['salidas anticipadas', 'salida anticipada', 'omisiones de salida'],
    opciones: [['evaluacion_salida__nombre', 'in', ['OMISIÓN DE SALIDA', 'SALIDA ANTICIPADA']]],
  },
  {
    id: 'f_checaron', etiqueta: 'Con checada real del reloj', frases: ['que checaron', 'checaron'], opciones: [['tipo_checador', 'exact', 'Fijo']],
  },
  {
    id: 'f_incapacidades',
    etiqueta: 'Solo incapacidades médicas',
    frases: ['incapacidades', 'incapacidad'],
    opciones: [['~es_una_incapacidad_medica', 'exact', true]],
  },
  {
    id: 'f_permisos', etiqueta: 'Solo permisos', frases: ['permisos', 'permiso'], opciones: [['~es_un_permiso', 'exact', true]],
  },
  {
    id: 'f_canceladas', etiqueta: 'Canceladas', frases: ['canceladas', 'cancelados', 'cancelada'], opciones: [['~estados_globales__descripcion', 'exact', 'Cancelado']],
  },
  {
    id: 'f_autorizadas', etiqueta: 'Autorizadas', frases: ['autorizadas', 'autorizados', 'autorizada'], opciones: [['~estados_globales__descripcion', 'exact', 'Autorizado']],
  },
  {
    id: 'f_pendientes',
    etiqueta: 'Pendientes de autorizar',
    frases: ['pendientes', 'por autorizar', 'sin autorizar'],
    opciones: [['~estados_globales__descripcion', 'exact', 'Activo']],
  },
  {
    id: 'f_eventuales', etiqueta: 'Nómina de eventuales', frases: ['eventuales'], opciones: [['~tipo_de_nomina_empleado__descripcion', 'exact', 'NOMINA EVENTUALES']],
  },
  {
    id: 'f_fallidas', etiqueta: 'Cargas fallidas', frases: ['fallidas', 'fallida', 'con error'], opciones: [['carga_exitosa', 'exact', false]],
  },
  {
    id: 'f_retroactivo', etiqueta: 'Sin retroactivos', frases: ['sin retroactivo', 'sin retroactivos'], opciones: [['sueldo_retroactivo', 'exact', false]],
  },
  {
    id: 'f_sin_foto',
    etiqueta: 'Sin foto',
    frases: ['sin foto', 'sin fotografia', 'no tienen foto'],
    opciones: [['tiene_foto', 'exact', false]],
  },
  {
    id: 'f_con_foto',
    etiqueta: 'Con foto',
    frases: ['con foto', 'tienen foto', 'tiene foto', 'con fotografia'],
    opciones: [['tiene_foto', 'exact', true]],
  },
  {
    id: 'f_sin_hijos',
    etiqueta: 'Sin hijos',
    frases: ['sin hijos'],
    opciones: [['tiene_hijos', 'exact', false]],
  },
  {
    id: 'f_con_hijos',
    etiqueta: 'Con hijos',
    frases: ['con hijos', 'tienen hijos'],
    opciones: [['tiene_hijos', 'exact', true]],
  },
  {
    id: 'f_estudios_concluidos',
    etiqueta: 'Con estudios concluidos',
    frases: ['con estudios concluidos', 'estudios concluidos'],
    opciones: [['estudios_concluidos', 'exact', true]],
  },
  {
    id: 'f_comisionados',
    etiqueta: 'Comisionados',
    frases: ['comisionados', 'en comision'],
    opciones: [['comisionado_en_area_externa', 'exact', true]],
  },
  {
    id: 'f_con_reloj',
    etiqueta: 'Aplica reloj checador',
    frases: ['con reloj checador', 'que checan en reloj', 'aplica reloj checador'],
    opciones: [['~aplica_reloj_checador', 'exact', true]],
  },
  {
    id: 'f_con_guardia',
    etiqueta: 'Aplica guardia',
    frases: ['que aplican guardia', 'con guardia'],
    opciones: [['~aplica_guardia', 'exact', true]],
  },
  {
    id: 'f_ocupadas',
    etiqueta: 'Plazas ocupadas',
    frases: ['ocupadas', 'ocupada'],
    opciones: [['disponible', 'exact', false]],
  },
  {
    id: 'f_disponibles',
    etiqueta: 'Plazas disponibles',
    frases: ['disponibles', 'vacantes', 'libres'],
    opciones: [['disponible', 'exact', true]],
  },
  {
    id: 'f_con_liquidacion',
    etiqueta: 'Con liquidación',
    frases: ['con liquidacion'],
    opciones: [['aplica_liquidacion', 'exact', true]],
  },
  {
    id: 'f_afiliacion_vigente',
    etiqueta: 'Afiliación vigente',
    frases: ['afiliaciones vigentes', 'afiliacion vigente'],
    opciones: [['estatus', 'exact', true]],
  },
  {
    id: 'f_suspension',
    etiqueta: 'Suspensión definitiva',
    frases: ['suspension definitiva', 'suspendidos definitivamente'],
    opciones: [['es_suspencion_definitiva', 'exact', true]],
  },
  {
    id: 'f_no_aplicadas',
    etiqueta: 'Sin aplicar en nómina',
    frases: ['no aplicadas', 'sin aplicar'],
    opciones: [['calculada', 'exact', false]],
  },
  {
    id: 'f_aplicadas',
    etiqueta: 'Aplicadas en nómina',
    frases: ['aplicadas en nomina', 'ya aplicadas', 'aplicadas'],
    opciones: [['calculada', 'exact', true]],
  },
  {
    id: 'f_con_retroactivo',
    etiqueta: 'Con retroactivo',
    frases: ['con retroactivo'],
    opciones: [['sueldo_retroactivo', 'exact', true], ['aplica_pago_de_retroactivo', 'exact', true]],
  },
  {
    id: 'f_solo_sueldo',
    etiqueta: 'Solo percepciones de sueldo',
    frases: ['solo sueldo', 'que son sueldo'],
    opciones: [['~es_sueldo', 'exact', true]],
  },
  {
    id: 'f_recurrentes',
    etiqueta: 'Recurrentes',
    frases: ['recurrentes'],
    opciones: [['~es_recurrente', 'exact', true]],
  },
  {
    id: 'f_afectan_presupuesto',
    etiqueta: 'Afectan presupuesto',
    frases: ['que afectan presupuesto', 'afectan presupuesto'],
    opciones: [['~afecta_presupuesto', 'exact', true]],
  },
  // Implícitos: no se escriben, los agrega una métrica.
  {
    id: 'f_desasignadas', etiqueta: 'Solo desasignadas', frases: [], opciones: [['fecha_desasignacion', 'gte', '1900-01-01']],
  },
  {
    id: 'f_autorizado_ap', etiqueta: 'Solo asignaciones vigentes (Autorizado)', frases: [], opciones: [['estados_globales__descripcion', 'exact', 'Autorizado']],
  },
];

// Nombres de dependencias o áreas escritos en la frase ("de la Oficialía Mayor").
const INICIO_NOMBRE_ORGANO = ['oficialia', 'tesoreria', 'sindicatura', 'presidencia', 'contraloria', 'comisaria',
  'jefatura', 'organo interno', 'ayuntamiento', 'secretaria general', 'direccion de', 'direccion general',
  'coordinacion general', 'coord gral', 'dir de'];

// ---------- Diccionario 4: formato ----------
const TIPOS = [
  { valor: 'pastel', etiqueta: 'Pastel', frases: ['pastel', 'pay', 'pie', 'dona', 'circular', 'proporcion', 'porcentaje'] },
  { valor: 'linea', etiqueta: 'Línea', frases: ['linea', 'lineas', 'evolucion', 'tendencia', 'comportamiento'] },
  { valor: 'barra_horizontal', etiqueta: 'Barras horizontales', frases: ['barras horizontales', 'horizontales', 'horizontal'] },
  { valor: 'barra', etiqueta: 'Barras', frases: ['barras verticales', 'barras', 'columnas'] },
];
const ORDENES = [
  { valor: 'valor_asc', etiqueta: 'Menor a mayor', frases: ['de menor a mayor', 'menor a mayor', 'ascendente', 'los menos', 'los de menos'] },
  { valor: 'valor_desc', etiqueta: 'Mayor a menor', frases: ['de mayor a menor', 'mayor a menor', 'descendente'] },
  { valor: 'etiqueta_asc', etiqueta: 'Alfabético', frases: ['orden alfabetico', 'alfabeticamente', 'alfabetico'] },
];
const OTROS = ['agrupa el resto', 'el resto en otros', 'con otros', 'y el resto', 'resto'];
const TIEMPOS = [
  {
    valor: 'ultimo',
    etiqueta: 'Último periodo de nómina calculado',
    frases: ['ultima quincena', 'ultimo periodo', 'ultima nomina', 'quincena actual', 'ultimo pago', 'periodo actual', 'esta quincena'],
  },
  {
    valor: 'historico',
    etiqueta: 'Sin filtro (histórico completo)',
    frases: ['todo el historico', 'historico completo', 'historico', 'desde siempre', 'todo el tiempo'],
  },
];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre',
  'noviembre', 'diciembre'];
const AGREGACIONES = [
  { valor: 'DISTINCT', frases: ['personas distintas', 'distintos', 'distintas', 'diferentes', 'sin repetir', 'unicos', 'unicas'] },
  { valor: 'AVG', frases: ['en promedio', 'promedio', 'promedios', 'media'] },
  { valor: 'MAX', frases: ['maximo', 'maxima', 'mas alto', 'mas alta'] },
  { valor: 'MIN', frases: ['minimo', 'minima', 'mas bajo', 'mas baja'] },
  { valor: 'SUM', frases: ['suma de', 'suma', 'total de', 'totales', 'total', 'monto', 'acumulado', 'cuanto suman'] },
  { valor: 'COUNT', frases: ['cuantos', 'cuantas', 'numero de', 'cantidad de', 'conteo'] },
];
export const ETIQUETA_AGREGACION = {
  SUM: 'Suma', AVG: 'Promedio', COUNT: 'Conteo', MIN: 'Mínimo', MAX: 'Máximo', DISTINCT: 'Contar distintos',
};
const PALABRAS_VACIAS = ('de del la las el los y o a en por para con sin que cual cuales como cuanto cuanta se lo le su sus '
  + 'un una unos unas al solo sola solamente nada mas me quiero ver dame muestrame grafica graficar grafique mostrar '
  + 'saber cada segun es son hay tiene tienen esta este estos estas ese esa entre sobre tipo nos mi mis tu tus '
  + 'agrupado agrupada agrupados agrupadas dividido separado').split(' ');
// ---------- Condiciones con números ----------
// "entre 30 y 40 años", "mayores de 50", "sueldo mayor a 20 mil", "más de 10 años de antigüedad".
// Se vuelven filtros (gte / lte / gt) sobre el campo numérico que corresponda.
const CAMPOS_NUMERICOS = {
  edad: { etiqueta: 'Edad', sel: ['edad'], entero: true },
  antiguedad: { etiqueta: 'Antigüedad', sel: ['~antiguedad_numerico_anos'], entero: true },
  sueldo: {
    etiqueta: 'Sueldo',
    sel: [`${DO}sueldo_mensual`, 'sueldo_mensual', 'sueldo_mensual_nominal_asignado',
      `${DO}plaza__sueldo_mensual_nominal_asignado`, 'sueldo_tabular_nominal', 'importe_de_solicitud'],
    entero: false,
  },
  minutos: { etiqueta: 'Minutos', sel: ['minutos_de_incidencia'], entero: true },
  dias: { etiqueta: 'Días', sel: ['dias_trabajados', 'numero_dias'], entero: true },
  sbc: { etiqueta: 'SBC', sel: ['salario_base_cotizacion_imss', 'importe_imss_sbc', 'sbc'], entero: false },
  neto: { etiqueta: 'Neto', sel: ['importe_neto'], entero: false },
};
const PALABRA_A_CAMPO_NUMERICO = {
  edad: 'edad',
  antiguedad: 'antiguedad',
  servicio: 'antiguedad',
  sueldo: 'sueldo',
  sueldos: 'sueldo',
  salario: 'sueldo',
  salarios: 'sueldo',
  pesos: 'sueldo',
  ganen: 'sueldo',
  ganan: 'sueldo',
  gana: 'sueldo',
  minutos: 'minutos',
  dias: 'dias',
  sbc: 'sbc',
  neto: 'neto',
};
const COMPARADORES = [
  ['entre', 'entre'],
  ['mayores de|mayor de|mayores a|mayor a|mayor que|mas de|arriba de|por encima de|superior a', 'mayor'],
  ['menores de|menor de|menores a|menor a|menor que|menos de|por debajo de|debajo de|inferior a', 'menor'],
  ['como maximo|hasta|maximo', 'hasta'],
  ['de al menos|como minimo|al menos|minimo|desde', 'desde'],
];
const NUMERO = '\\$?\\s?(\\d[\\d.,]*)\\s*(mil)?';
const PATRON_CONDICION = new RegExp(
  '\\s(?:(?:con|de|que tengan|que tienen)\\s+)?(?:(?:una|un|el|la)\\s+)?'
  + '(?:(edad|antiguedad|sueldos?|salarios?|sbc|neto|ganen|ganan|gana)\\s+(?:de\\s+)?)?'
  + `(${COMPARADORES.map(([p]) => p).join('|')})\\s+${NUMERO}(?:\\s+(?:y|a)\\s+${NUMERO})?`
  + '(?:\\s+(anos|ano|pesos|minutos|dias))?(?:\\s+(?:de\\s+)?(edad|antiguedad|servicio|sueldo|salario))?(?=\\s)',
);

const aNumero = (texto, mil) => {
  let limpio = String(texto).replace(/[$\s]/g, '');
  if (/[.,]\d{3}(?!\d)/.test(limpio)) limpio = limpio.replace(/[.,](?=\d{3}(?!\d))/g, '');
  const n = parseFloat(limpio.replace(',', '.'));
  return mil ? n * 1000 : n;
};

const extraerCondicionesNumericas = (T, marcar, avisos) => {
  const condiciones = [];
  let m = T.consumir(PATRON_CONDICION);
  while (m) {
    const [texto, campoAntes, comparador, n1, mil1, n2, mil2, unidad, campoDespues] = m;
    const tipo = COMPARADORES.find(([p]) => new RegExp(`^(?:${p})$`).test(comparador))[1];
    let clave = PALABRA_A_CAMPO_NUMERICO[campoAntes] || PALABRA_A_CAMPO_NUMERICO[campoDespues];
    if (!clave && (unidad === 'anos' || unidad === 'ano')) clave = 'edad';
    if (!clave && unidad) clave = PALABRA_A_CAMPO_NUMERICO[unidad];
    const a = aNumero(n1, mil1);
    if (!clave && a >= 1000) clave = 'sueldo';
    if (!clave) {
      avisos.push(`No supe a qué se refiere «${texto.trim()}»; di si es edad, sueldo, antigüedad o minutos.`);
    } else {
      const campo = CAMPOS_NUMERICOS[clave];
      const paso = campo.entero ? 1 : 0.01;
      const fmt = (v) => v.toLocaleString('es-MX');
      const crear = (operador, valor, simbolo) => ({
        id: `num_${condiciones.length}`,
        etiqueta: `${campo.etiqueta} ${simbolo} ${fmt(valor)}`,
        frases: [texto.trim()],
        opciones: campo.sel.map((s) => [s, operador, valor]),
      });
      if (tipo === 'entre' && n2) {
        const b = aNumero(n2, mil2);
        condiciones.push(crear('gte', Math.min(a, b), '≥'), crear('lte', Math.max(a, b), '≤'));
      } else if (tipo === 'mayor') {
        condiciones.push(crear('gt', a, '>'));
      } else if (tipo === 'menor') {
        // No existe el operador "menor que": se usa "menor o igual" al valor anterior.
        condiciones.push(crear('lte', a - paso, '≤'));
        avisos.push(`«${texto.trim()}»: como no existe «menor que», usé «menor o igual a ${fmt(a - paso)}».`);
      } else if (tipo === 'hasta') {
        condiciones.push(crear('lte', a, '≤'));
      } else {
        condiciones.push(crear('gte', a, '≥'));
      }
      marcar('Filtro', texto.trim(), condiciones.slice(-(tipo === 'entre' && n2 ? 2 : 1)).map((c) => c.etiqueta).join(' y '));
    }
    m = T.consumir(PATRON_CONDICION);
  }
  return condiciones;
};

const PREFIJOS_DIMENSION = ['por', 'segun', 'de cada', 'para cada', 'agrupado por', 'agrupada por', 'agrupados por',
  'dividido por', 'separado por'];

export const normalizar = (texto) => String(texto || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9.,$%\s]/g, ' ')
  // La coma separa ideas ("por área, solo sindicalizados") salvo dentro de un número ("15,000").
  .replace(/,(?!\d)/g, ' , ')
  .replace(/\s+/g, ' ')
  .trim();

const coincide = (campo, selector) => {
  if (selector.charAt(0) === '~') {
    const sufijo = selector.slice(1);
    return campo === sufijo || (campo.length > sufijo.length && campo.endsWith(sufijo)
      && campo.charAt(campo.length - sufijo.length - 1) === '_');
  }
  if (selector.charAt(0) === '^') return campo.startsWith(selector.slice(1));
  return campo === selector;
};

const porLargo = (lista) => {
  const pares = [];
  lista.forEach((concepto) => {
    (concepto.frases || []).forEach((frase) => pares.push([frase, concepto]));
  });
  return pares.sort((a, b) => b[0].length - a[0].length);
};

const escaparRegex = (texto) => texto.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// ---------- Corrector de escritura ----------
// Acentos y mayúsculas ya se ignoran. Además: atajos ("x" = "por", "anio" = "año") y
// faltas comunes ("pusto", "sindicalisados", "quinsena"): cada palabra desconocida se
// cambia por la más parecida del diccionario (1 letra de diferencia en palabras
// cortas, 2 en largas).
const ATAJOS = [
  [/\sx\s/g, ' por '],
  [/\spx\s/g, ' por '],
  [/\sq\s/g, ' que '],
  [/\snum\s/g, ' numero '],
  [/\sanios?(?=\s)/g, (m) => m.replace('anio', 'ano')],
];

let vocabularioCache = null;
const vocabulario = () => {
  if (vocabularioCache) return vocabularioCache;
  const palabras = new Set();
  const agregar = (frase) => String(frase).split(' ').forEach((w) => { if (w.length >= 3) palabras.add(w); });
  [METRICAS, DIMENSIONES, FILTROS, TIPOS, ORDENES, TIEMPOS, AGREGACIONES, AGRUPACIONES_FECHA]
    .forEach((lista) => lista.forEach((c) => (c.frases || []).forEach(agregar)));
  OTROS.concat(
    INICIO_NOMBRE_ORGANO,
    MESES,
    PALABRAS_VACIAS,
    ['piramide', 'poblacional', 'top', 'rangos', 'rango',
      'grupos', 'tramos', 'mayores', 'principales', 'primeros', 'primeras', 'administrativa', 'mayor'],
    // Palabras de las condiciones con números
    Object.keys(PALABRA_A_CAMPO_NUMERICO),
    ['anos', 'mayor', 'menores', 'menor', 'menos', 'arriba', 'encima',
      'debajo', 'superior', 'inferior', 'hasta', 'maximo', 'minimo', 'desde', 'menos', 'tengan', 'tienen'],
  )
    .forEach(agregar);
  vocabularioCache = Array.from(palabras);
  return vocabularioCache;
};

// Distancia de edición (con transposición), cortada en `tope` para ir rápido.
const distancia = (a, b, tope) => {
  if (Math.abs(a.length - b.length) > tope) return tope + 1;
  let anterior2 = null;
  let anterior = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i += 1) {
    const actual = [i];
    let minimoFila = i;
    for (let j = 1; j <= b.length; j += 1) {
      const costo = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
      actual[j] = Math.min(anterior[j] + 1, actual[j - 1] + 1, anterior[j - 1] + costo);
      const transpuestas = i > 1 && j > 1
        && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1);
      if (anterior2 && transpuestas) {
        actual[j] = Math.min(actual[j], anterior2[j - 2] + 1);
      }
      minimoFila = Math.min(minimoFila, actual[j]);
    }
    if (minimoFila > tope) return tope + 1;
    anterior2 = anterior;
    anterior = actual;
  }
  return anterior[b.length];
};

const corregirEscritura = (texto, correcciones) => {
  let t = ` ${texto} `;
  ATAJOS.forEach(([patron, reemplazo]) => { t = t.replace(patron, reemplazo); });
  const vocab = vocabulario();
  const conocidas = new Set(vocab);
  return t.trim().split(' ').map((w) => {
    if (!w || w.length < 4 || conocidas.has(w) || /\d/.test(w) || w === ',') return w;
    const tope = w.length >= 7 ? 2 : 1;
    // Empates: gana la que empieza igual y luego la del mismo largo.
    const puntos = (v) => (v.charAt(0) === w.charAt(0) ? 2 : 0) + (v.length === w.length ? 1 : 0);
    let mejor = null;
    let mejorDistancia = tope + 1;
    vocab.forEach((v) => {
      const d = distancia(w, v, tope);
      if (d < mejorDistancia || (d === mejorDistancia && mejor && puntos(v) > puntos(mejor))) {
        mejor = v;
        mejorDistancia = d;
      }
    });
    if (mejor && mejorDistancia <= tope) {
      correcciones.push({ de: w, a: mejor });
      return mejor;
    }
    return w;
  }).join(' ');
};

// Texto de trabajo: cada frase encontrada se "consume" (se cambia por espacios).
const crearTexto = (inicial) => {
  let t = ` ${inicial} `;
  return {
    get: () => t,
    set: (nuevo) => { t = nuevo; },
    buscar: (frase, prefijos) => {
      const pref = prefijos ? `(?:${prefijos.join('|')})\\s+(?:el |la |los |las |su |sus )?` : '';
      const m = new RegExp(`(\\s)${pref}${escaparRegex(frase)}(?=\\s)`).exec(t);
      if (!m) return null;
      const encontrado = m[0].slice(1);
      t = t.slice(0, m.index + 1) + ' '.repeat(encontrado.length) + t.slice(m.index + m[0].length);
      return encontrado.trim();
    },
    consumir: (patron) => {
      const m = patron.exec(t);
      if (!m) return null;
      t = t.slice(0, m.index) + ' '.repeat(m[0].length) + t.slice(m.index + m[0].length);
      return m;
    },
  };
};

/**
 * Prepara el traductor con el catálogo que regresa la API
 * (`{id, modelo_app_label, campo, etiqueta_visible, tipo_dato, es_dimension,
 * es_metrica, agregaciones_permitidas}`) y regresa la función `traducir(frase)`.
 */
export const crearTraductor = (catalogo) => {
  const porTabla = {};
  (catalogo || []).forEach((e) => {
    (porTabla[e.modelo_app_label] = porTabla[e.modelo_app_label] || []).push(e);
  });

  const buscarEnTabla = (ancla, selectores, condicion) => {
    const lista = porTabla[ancla] || [];
    let encontrado = null;
    selectores.some((selector) => {
      encontrado = lista.find((e) => coincide(e.campo, selector) && (!condicion || condicion(e))
        // "Área" no debe tomar el área superior.
        && !(selector === '~directorio_de_area__nombre' && e.campo.includes('directorio_area_padre'))) || null;
      return !!encontrado;
    });
    return encontrado;
  };

  return (original) => {
    const r = {
      ok: false,
      errores: [],
      avisos: [],
      notas: [],
      entendido: [],
      noEntendi: [],
      correcciones: [],
      config: null,
    };
    let limpio = normalizar(original);
    if (!limpio) {
      r.errores.push('Escribe qué quieres graficar, por ejemplo: «cuántos empleados por dependencia».');
      return r;
    }
    limpio = corregirEscritura(limpio, r.correcciones);
    const T = crearTexto(limpio);
    const marcar = (categoria, texto, significado) => {
      r.entendido.push({ categoria, texto, significado });
    };

    // 0. Condiciones con números ("entre 30 y 40 años", "sueldo mayor a 20 mil")
    const condiciones = extraerCondicionesNumericas(T, marcar, r.avisos);

    // 1. Números: top N, rangos de N
    let limite = null;
    let ancho = null;
    let m = T.consumir(/\s(?:top|los|las|primeros|primeras|principales)\s+(\d{1,3})(?:\s+(?:mas|mayores|principales|primeros|primeras))?(?=\s)/)
      || T.consumir(/\s(\d{1,3})\s+(?:principales|mayores|primeros|primeras|mas altos|mas altas)(?=\s)/);
    if (m) {
      limite = parseInt(m[1], 10);
      marcar('Formato', m[0].trim(), `Límite: ${limite}`);
    }
    m = T.consumir(/\s(?:en\s+)?(?:rangos?|grupos?|tramos?)\s+de\s+(\d[\d.,]*)\s*(mil)?(?=\s)/)
      || T.consumir(/\scada\s+(\d[\d.,]*)\s*(mil)?(?=\s)/);
    if (m) {
      ancho = parseFloat(m[1].replace(/[.,](?=\d{3}\b)/g, '').replace(',', '.')) * (m[2] ? 1000 : 1);
      marcar('Formato', m[0].trim(), `Rangos de ${ancho.toLocaleString('es-MX')}`);
    }

    // 2. Formato y periodo
    let tipo = null;
    let orden = null;
    let otros = false;
    let tiempo = null;
    const periodoMencionado = [];
    porLargo(ORDENES).some(([frase, c]) => {
      const t = T.buscar(frase);
      if (t) { orden = c.valor; marcar('Formato', t, `Orden: ${c.etiqueta}`); }
      return !!t;
    });
    porLargo(TIPOS).some(([frase, c]) => {
      const t = T.buscar(frase, ['en', 'como', 'de', 'tipo', 'grafica de']) || T.buscar(frase);
      if (t) { tipo = c.valor; marcar('Formato', t, `Tipo: ${c.etiqueta}`); }
      return !!t;
    });
    OTROS.some((frase) => {
      const t = T.buscar(frase);
      if (t) { otros = true; marcar('Formato', t, 'Agrupar el resto en «Otros»'); }
      return !!t;
    });
    porLargo(TIEMPOS).some(([frase, c]) => {
      const t = T.buscar(frase, ['en la', 'en el', 'de la', 'del']) || T.buscar(frase);
      if (t) { tiempo = c.valor; marcar('Periodo', t, c.etiqueta); }
      return !!t;
    });
    MESES.forEach((mes) => {
      const t = T.buscar(mes, ['en', 'de', 'desde', 'hasta']) || T.buscar(mes);
      if (t) periodoMencionado.push(t);
    });
    m = T.consumir(/\s(?:en|de|del|desde|hasta)?\s*(20\d\d)(?=\s)/);
    while (m) {
      periodoMencionado.push(m[1]);
      m = T.consumir(/\s(?:en|de|del|desde|hasta)?\s*(20\d\d)(?=\s)/);
    }
    if (periodoMencionado.length) marcar('Periodo', periodoMencionado.join(', '), 'Se elige en el selector de fechas de la tarjeta');

    // 3. Dimensión (lo que va después de "por", "según", "de cada"...)
    let dimension = null;
    let agrupacionFecha = null;
    let especial = null;
    if (T.buscar('piramide de edad') || T.buscar('piramide de edades') || T.buscar('piramide poblacional')) {
      especial = 'piramide';
      dimension = DIMENSIONES.find((d) => d.id === 'edad');
      marcar('Agrupar por', 'pirámide de edad', 'Edad en rangos de 5 años');
    }
    if (!dimension) {
      porLargo(AGRUPACIONES_FECHA).some(([frase, c]) => {
        const t = T.buscar(frase);
        if (t) { agrupacionFecha = c.valor; marcar('Agrupar por', t, `Fecha agrupada ${c.etiqueta.toLowerCase()}`); }
        return !!t;
      });
    }
    const dimensionesExtra = [];
    if (!dimension && !agrupacionFecha) {
      porLargo(DIMENSIONES).forEach(([frase, c]) => {
        const t = T.buscar(frase, PREFIJOS_DIMENSION);
        if (!t) return;
        if (!dimension) {
          dimension = c;
          marcar('Agrupar por', t, c.etiqueta);
        } else if (dimension.id !== c.id) dimensionesExtra.push(c.etiqueta);
      });
    }
    // "por estado civil y género": la segunda va después de "y".
    if (dimension || agrupacionFecha) {
      const frasesDeMetrica = porLargo(METRICAS).map(([frase]) => frase);
      porLargo(DIMENSIONES).forEach(([frase, c]) => {
        if ((dimension && c.id === dimension.id) || frasesDeMetrica.includes(frase)) return;
        const t = T.buscar(frase, ['y por', 'y', ', y', 'y tambien por']);
        if (t && !dimensionesExtra.includes(c.etiqueta)) dimensionesExtra.push(c.etiqueta);
      });
    }
    if (dimensionesExtra.length) {
      r.avisos.push(`Solo se puede agrupar por una cosa a la vez. Usé «${dimension ? dimension.etiqueta : 'la fecha'}»; `
        + `«${dimensionesExtra.join('», «')}» tendría que ir como filtro o en otra gráfica.`);
    }
    // Sin "por": un campo de agrupación escrito suelto ("foto", "género", "tiene hijos")
    // se toma como la dimensión. Se intenta antes de los filtros para que "tiene foto"
    // agrupe por Sí/No en vez de filtrar.
    if (!dimension && !agrupacionFecha && !/\s(?:por|segun|cada)\s/.test(` ${limpio} `)) {
      const frasesDeMetrica = porLargo(METRICAS).map(([frase]) => frase);
      porLargo(DIMENSIONES).some(([frase, c]) => {
        if (frasesDeMetrica.includes(frase)) return false;
        const t = T.buscar(frase);
        if (t) {
          dimension = c;
          marcar('Agrupar por', t, c.etiqueta);
          r.avisos.push(`No escribiste «por …»; usé «${c.etiqueta}» para agrupar. `
            + 'Si querías filtrar, escribe además por qué agrupar (p. ej. «… por área»).');
        }
        return !!t;
      });
    }

    // 4. Nombre de dependencia o área escrito en la frase
    let filtroNombre = null;
    INICIO_NOMBRE_ORGANO.some((inicio) => {
      const patron = new RegExp('\\s(?:(?:solo|solamente)\\s+)?(?:(?:de|del|en|en la|en el|de la)\\s+)?(?:la\\s+|el\\s+)?'
        + `((?:area|dependencia)\\s+(?:de\\s+)?)?(${inicio}(?:\\s+(?!por\\b|en\\b|solo\\b|top\\b|con\\b|y\\b|desde\\b|hasta\\b|,)`
        + '[a-z0-9.]+){0,6})(?=\\s)');
      const mm = patron.exec(T.get());
      if (!mm) return false;
      const antes = T.get().slice(Math.max(0, mm.index - 4), mm.index + 1);
      if (/\spor\s?$/.test(antes)) return false;
      const t = T.get();
      T.set(t.slice(0, mm.index) + ' '.repeat(mm[0].length) + t.slice(mm.index + mm[0].length));
      filtroNombre = { esArea: !!(mm[1] && mm[1].startsWith('area')), valor: mm[2].toUpperCase() };
      marcar('Filtro', mm[0].trim(), `${filtroNombre.esArea ? 'Área' : 'Dependencia'} = ${filtroNombre.valor}`);
      return true;
    });

    // 5. Filtros con palabras
    const filtros = [];
    porLargo(FILTROS).forEach(([frase, c]) => {
      if (filtros.includes(c)) return;
      const t = T.buscar(frase, ['solo', 'solamente', 'de los', 'de las', 'los', 'las', 'que sean', 'con']) || T.buscar(frase);
      if (t) { filtros.push(c); marcar('Filtro', t, c.etiqueta); }
    });
    filtros.push(...condiciones);

    // 6. Agregación y métricas
    let agregacionPedida = null;
    const prioridad = AGREGACIONES.map((a) => a.valor);
    porLargo(AGREGACIONES).forEach(([frase, c]) => {
      const t = T.buscar(frase);
      if (!t) return;
      if (!agregacionPedida || prioridad.indexOf(c.valor) < prioridad.indexOf(agregacionPedida)) {
        agregacionPedida = c.valor;
      }
      marcar('Qué medir', t, ETIQUETA_AGREGACION[c.valor]);
    });
    let metricas = [];
    const grupos = {};
    porLargo(METRICAS).forEach(([frase, c]) => {
      if ((grupos[c.grupo] && c.grupo !== 'conteo') || metricas.includes(c)) {
        T.buscar(frase);
        return;
      }
      const t = T.buscar(frase);
      if (!t || (c.grupo === 'conteo' && grupos.conteo)) return;
      grupos[c.grupo] = true;
      metricas.push(c);
      marcar('Qué medir', t, c.etiqueta);
    });
    // Si mide algo (dinero, edad…) "de los empleados", "empleados" no es otra métrica.
    if (metricas.length > 1) metricas = metricas.filter((mt) => mt.id !== 'personas');
    metricas = metricas.slice(0, 3);
    if (!dimension && !agrupacionFecha) {
      r.errores.push('No encontré por qué agrupar. Usa «por …», por ejemplo: «por dependencia», «por género», '
        + '«por mes» o «por rango de sueldo».');
    }
    T.get().split(/\s+/).forEach((w) => {
      if (w && w !== ',' && w.length > 2 && !PALABRAS_VACIAS.includes(w) && !/^\d+$/.test(w)) r.noEntendi.push(w);
    });
    if (r.errores.length) return r;

    // 7. Elegir la tabla que tenga todo junto
    const filtroImplicito = (id) => FILTROS.find((f) => f.id === id);
    const candidatas = [];
    Object.keys(TABLAS).forEach((ancla) => {
      let dimEntrada = null;
      if (agrupacionFecha) {
        let porMetrica = null;
        metricas.forEach((mt) => {
          if (mt.fecha && mt.fecha[ancla]) porMetrica = mt.fecha[ancla];
        });
        const campoFecha = porMetrica || FECHA_PRINCIPAL[ancla];
        dimEntrada = campoFecha ? buscarEnTabla(ancla, [campoFecha]) : null;
      } else {
        dimEntrada = buscarEnTabla(ancla, dimension.sel, (e) => e.es_dimension);
      }
      if (!dimEntrada) return;
      const mets = [];
      let faltaMetrica = false;
      let preferenciaMetrica = 0;
      metricas.forEach((mt) => {
        let encontrada = null;
        mt.campos.some(([anclaCampo, selector, agg], indice) => {
          if (anclaCampo !== '*' && anclaCampo !== ancla) return false;
          const e = buscarEnTabla(ancla, [selector], (x) => x.es_metrica);
          if (e) {
            encontrada = { entrada: e, agg, concepto: mt };
            // La primera tabla de la lista de la métrica es la preferida.
            preferenciaMetrica += indice;
          }
          return !!e;
        });
        if (encontrada) mets.push(encontrada); else faltaMetrica = true;
      });
      if (faltaMetrica) return;
      if (!mets.length) {
        const conteo = buscarEnTabla(ancla, ['id'], (x) => x.es_metrica);
        const distintos = buscarEnTabla(ancla, ['empleado', 'nomina_plaza__empleado'], (x) => x.es_metrica);
        if (conteo) mets.push({ entrada: conteo, agg: 'COUNT', concepto: null });
        else if (distintos) mets.push({ entrada: distintos, agg: 'DISTINCT', concepto: null });
        else return;
      }
      const filtrosOk = [];
      const filtrosFalta = [];
      const implicitos = [];
      metricas.forEach((mt) => {
        (mt.filtros || []).forEach((id) => implicitos.push(filtroImplicito(id)));
      });
      filtros.concat(implicitos).forEach((f) => {
        let hecho = null;
        f.opciones.some(([selector, operador, valor]) => {
          const e = buscarEnTabla(ancla, [selector]);
          if (e) {
            hecho = {
              filtro: f, entrada: e, operador, valor,
            };
          }
          return !!e;
        });
        if (hecho) {
          filtrosOk.push(hecho);
          (f.extra || []).forEach(([selector, operador, valor]) => {
            const e = buscarEnTabla(ancla, [selector]);
            if (e) {
              filtrosOk.push({
                filtro: f, entrada: e, operador, valor, extra: true,
              });
            }
          });
        } else if (f.frases.length) filtrosFalta.push(f.etiqueta);
      });
      let nombreOk = null;
      if (filtroNombre) {
        nombreOk = filtroNombre.esArea
          ? buscarEnTabla(ancla, ['~directorio_de_area__nombre']) : buscarEnTabla(ancla, ['^dependencia:']);
        if (!nombreOk) filtrosFalta.push(`${filtroNombre.esArea ? 'Área' : 'Dependencia'} = ${filtroNombre.valor}`);
      }
      const puntaje = (PRIORIDAD_TABLA[ancla] || 2)
        - filtrosFalta.length * 50
        + filtrosOk.length * 3
        + (metricas.length ? 20 : 0)
        - preferenciaMetrica * 5;
      candidatas.push({
        ancla, dimEntrada, mets, filtrosOk, filtrosFalta, nombreOk, puntaje,
      });
    });

    if (!candidatas.length) {
      const queMide = metricas.map((mt) => `«${mt.etiqueta}»`).join(' y ');
      const porQue = agrupacionFecha ? 'una fecha' : `«${dimension.etiqueta}»`;
      r.errores.push(`No hay una tabla que tenga ${queMide || 'esa medida'} y ${porQue} juntos. `
        + 'Prueba agrupar por otra cosa o arma la gráfica a mano.');
      return r;
    }
    candidatas.sort((a, b) => b.puntaje - a.puntaje);
    const g = candidatas[0];
    g.filtrosFalta.forEach((f) => r.avisos.push(`El filtro «${f}» no existe en la tabla ${TABLAS[g.ancla][0]}; lo omití.`));

    // 8. Armar la configuración (con ids del catálogo, lista para el formulario)
    const numerica = g.dimEntrada.tipo_dato === 'numerico';
    const cfg = {
      ancla: g.ancla,
      tabla: TABLAS[g.ancla][0],
      modulo: TABLAS[g.ancla][1],
      dimension: g.dimEntrada,
      agrupacionFecha: agrupacionFecha || null,
      anchoRango: null,
      metricas: [],
      filtros: [],
      campoFecha: null,
      filtroFechaPorDefecto: 'ninguno',
      orden: 'valor_desc',
      limite: null,
      otros: false,
      tipo: null,
      nombre: '',
    };
    if (agrupacionFecha) cfg.orden = 'etiqueta_asc';
    if (numerica) {
      cfg.anchoRango = ancho || (dimension && dimension.ancho) || (especial === 'piramide' ? 5 : null);
      cfg.orden = 'etiqueta_asc';
    } else if (ancho) {
      r.avisos.push('Los rangos solo aplican a números (edad, sueldo, antigüedad, minutos); los ignoré.');
    }
    if (dimension && dimension.orden) cfg.orden = dimension.orden;

    g.mets.forEach((x) => {
      let { agg } = x;
      const esConteo = x.concepto && x.concepto.grupo === 'conteo';
      const permitidas = x.entrada.agregaciones_permitidas || [];
      if (agregacionPedida && !esConteo && permitidas.includes(agregacionPedida)) {
        agg = agregacionPedida;
      } else if (agregacionPedida === 'DISTINCT' && x.entrada.campo === 'id') {
        const dist = buscarEnTabla(g.ancla, ['empleado', 'nomina_plaza__empleado', 'plaza'], (e) => e.es_metrica);
        if (dist) {
          cfg.metricas.push({ entrada: dist, agg: 'DISTINCT', titulo: null });
          return;
        }
      } else if (agregacionPedida && !esConteo && agregacionPedida !== 'COUNT') {
        r.avisos.push(`«${x.entrada.etiqueta_visible}» no admite ${ETIQUETA_AGREGACION[agregacionPedida]}; `
          + `usé ${ETIQUETA_AGREGACION[agg]}.`);
      }
      if (!permitidas.includes(agg)) [agg] = permitidas;
      const titulo = x.concepto ? x.concepto.etiqueta : null;
      cfg.metricas.push({ entrada: x.entrada, agg, titulo });
    });

    g.filtrosOk.forEach((f) => {
      let nota = '';
      if (f.extra) nota = 'Agregado para no contar a quien no checa';
      else if (!f.filtro.frases.length) nota = 'Agregado automáticamente';
      cfg.filtros.push({
        entrada: f.entrada,
        operador: f.operador,
        valor: f.valor,
        nota,
        titulo: f.extra || !f.filtro.frases.length ? null : f.filtro.etiqueta,
      });
    });
    if (g.nombreOk) {
      cfg.filtros.push({
        entrada: g.nombreOk,
        operador: 'exact',
        valor: filtroNombre.valor,
        buscarValorExacto: true,
        nota: 'Revisa que sea el nombre exacto; si es un área y no una dependencia, cambia el campo a «Área»',
        titulo: filtroNombre.valor.charAt(0) + filtroNombre.valor.slice(1).toLowerCase(),
      });
    }
    // Personal vigente por defecto en conteos de Empleado
    const hayFiltroDeEstado = cfg.filtros.some((f) => f.entrada.campo === 'estado_del_empleado__descripcion');
    if (g.ancla === 'entidad.Empleado' && !hayFiltroDeEstado && !(dimension && dimension.id === 'estado_empleado')) {
      const estado = buscarEnTabla(g.ancla, ['estado_del_empleado__descripcion']);
      if (estado) {
        cfg.filtros.unshift({
          entrada: estado, operador: 'in', valor: VIGENTES, nota: 'Agregado para no contar bajas', titulo: null,
        });
      }
    }
    if (dimension && dimension.filtroValores) {
      const campoValores = buscarEnTabla(g.ancla, dimension.sel);
      if (campoValores && !cfg.filtros.some((f) => f.entrada.id === campoValores.id)) {
        cfg.filtros.push({
          entrada: campoValores,
          operador: 'in',
          valor: dimension.filtroValores,
          nota: 'Este campo solo se llena en las excepciones; el filtro evita una barra enorme de «Sin especificar»',
          titulo: null,
        });
      }
    }

    // Fechas
    if (TABLAS_DE_NOMINA.includes(g.ancla) && !agrupacionFecha && !(dimension && dimension.serie)) {
      const ff = buscarEnTabla(g.ancla, [FECHA_FINAL_NOMINA]);
      if (ff) {
        cfg.campoFecha = ff;
        cfg.filtroFechaPorDefecto = tiempo === 'historico' ? 'ninguno' : 'ultimo_periodo_calculado';
        if (!tiempo && !periodoMencionado.length) {
          r.notas.push('Puse «Último periodo de nómina calculado» para no sumar todas las quincenas.');
        }
      }
    } else if (agrupacionFecha) {
      cfg.campoFecha = g.dimEntrada;
    } else if (FECHA_PRINCIPAL[g.ancla] && (periodoMencionado.length || tiempo)) {
      cfg.campoFecha = buscarEnTabla(g.ancla, [FECHA_PRINCIPAL[g.ancla]]);
    }
    if (periodoMencionado.length) {
      r.notas.push(`El periodo («${periodoMencionado.join(', ')}») se elige en el selector de fechas de la tarjeta.`);
    }
    if (tiempo === 'ultimo' && cfg.filtroFechaPorDefecto !== 'ultimo_periodo_calculado') {
      r.avisos.push('«Último periodo» solo aplica a las tablas de nómina.');
    }

    // Orden, límite y «Otros»
    if (orden) cfg.orden = orden;
    if (limite) {
      cfg.limite = limite;
      if (!orden) cfg.orden = 'valor_desc';
    } else if (dimension && dimension.limite && !numerica && !agrupacionFecha) {
      cfg.limite = dimension.limite;
      r.notas.push(`Puse límite ${dimension.limite} porque «${dimension.etiqueta}» suele tener muchas categorías.`);
    }
    const agrupables = cfg.metricas.every((x) => x.agg === 'SUM' || x.agg === 'COUNT');
    const limitePorDefecto = dimension && dimension.limite && !limite;
    if (cfg.limite && agrupables && (otros || limitePorDefecto)) cfg.otros = true;
    if (otros && !cfg.limite) r.avisos.push('«Otros» necesita un límite (Top N); agrega, por ejemplo, «top 10».');
    if (otros && cfg.limite && !agrupables) r.avisos.push('«Otros» solo funciona con Suma o Conteo.');

    // Tipo de gráfica
    const una = cfg.metricas.length === 1;
    if (tipo && (tipo === 'linea' || tipo === 'pastel') && !una) {
      r.avisos.push('Línea y pastel admiten una sola métrica; con varias usé Barras.');
      tipo = 'barra';
    }
    if (!tipo) {
      if (agrupacionFecha || (dimension && dimension.serie)) tipo = una ? 'linea' : 'barra';
      else if (numerica) tipo = 'barra';
      else if (dimension && dimension.pocos && una && agrupables) tipo = 'pastel';
      else if (dimension && dimension.muchas) tipo = 'barra_horizontal';
      else tipo = 'barra';
    }
    cfg.tipo = tipo;

    // Nombre sugerido
    const nombreMetricas = cfg.metricas.map((x) => {
      const base = x.titulo || x.entrada.etiqueta_visible;
      if (x.agg === 'COUNT' && /^n[uú]mero de /i.test(base)) return base.replace(/^n[uú]mero de /i, '');
      return x.agg === 'AVG' ? `${base} promedio` : base;
    }).join(' y ');
    let porTexto = `por ${numerica ? 'Rango de ' : ''}${g.dimEntrada.etiqueta_visible}`;
    if (agrupacionFecha) porTexto = { mes: 'por Mes', anio: 'por Año', dia: 'por Día' }[agrupacionFecha];
    const conFiltros = cfg.filtros.map((f) => f.titulo && f.titulo.replace(/\s*\(.*\)$/, '')).filter(Boolean);
    cfg.nombre = `${nombreMetricas.charAt(0).toUpperCase()}${nombreMetricas.slice(1)} ${porTexto}`
      + `${conFiltros.length ? ` (${conFiltros.join(', ')})` : ''}${cfg.limite ? ` — Top ${cfg.limite}` : ''}`;
    if (g.ancla === 'nominas.NominaPlaza' && cfg.metricas.some((x) => /percepciones/i.test(x.entrada.etiqueta_visible))) {
      r.notas.push('No incluye cuotas patronales (IMSS, IPEJAL): viven en otras tablas.');
    }
    r.config = cfg;
    r.ok = true;
    return r;
  };
};
